import React, { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";import {
  Handshake, ShieldCheck, ArrowRight, Loader2, LogOut, FileCheck2, Percent,
  UploadCloud, FileText, CheckCircle2, XCircle, Clock, Truck, PenLine,
  AlertTriangle, Ban, ClipboardCheck, Building2, Landmark, ClipboardList, Copy, Check, X, Pencil, Trash2, CreditCard, KeyRound, Repeat,
} from "lucide-react";
import { SupportMessaging } from "../components/SupportMessaging";
import { InboxBell } from "../components/InboxBell";
import SubmitToLender from "../components/SubmitToLender";
import { toast } from "sonner";
import { US_STATES, instrumentForState, gaIntangibleTax, recommendedNoteStructure } from "../lib/securityInstrument";
import { SealWatermark } from "../components/SealWatermark";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
import AccountDetailsPanel from "../components/AccountDetailsPanel";
import BrokerLinks from "../components/BrokerLinks";
import { CounterpartyAutocomplete } from "../components/CounterpartyAutocomplete";
const TKEY = "th_broker_token";
const fmt = (n) => (n == null ? "—" : `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 })}`);
const errMsg = (e) => {
  const d = e?.response?.data?.detail;
  if (Array.isArray(d)) return d.map((x) => x.msg || JSON.stringify(x)).join(" ");
  return d || e.message || "Something went wrong.";
};

const POLICIES = [
  { k: "arms_length", t: "Arm's-length conduct", d: "I will present only true, complete, and verified borrower information. I will never falsify income, identity, occupancy, or property data." },
  { k: "kyc", t: "KYC / identity verification", d: "I will collect and upload valid government ID and supporting documents for every client before submitting for approval." },
  { k: "refi_only", t: "0% Refinance — mortgages only", d: "The broker channel is strictly for 0% refinance of residential and commercial mortgage obligations. No other product may be submitted through this desk." },
  { k: "no_guarantee", t: "No guarantees of approval", d: "I will not promise borrowers approval, funding timelines, or outcomes. All financing is subject to Seutrade Financial Services underwriting and approval." },
  { k: "no_fees", t: "No unauthorized fees", d: "I will not collect any upfront fee from borrowers. Broker compensation is paid solely by Seutrade Financial Services per the published commission schedule." },
  { k: "confidentiality", t: "Confidentiality & data protection", d: "I will safeguard all borrower PII, will not disclose deal documents without authorization, and will use uploads only for this financing." },
  { k: "compliance", t: "Regulatory compliance", d: "I will comply with all applicable federal and state lending, licensing, fair-lending, and anti-fraud laws." },
  { k: "hold_harmless", t: "Hold Harmless & Indemnification", d: "I shall indemnify, defend, and hold harmless SEUTRADE FINANCIAL SERVICES, its officers, directors, trustees, agents, successors, and assigns from and against any and all claims, losses, damages, and liabilities arising from my acts, omissions, breach, or default." },
  { k: "waiver_of_claims", t: "Waiver & Release of Claims", d: "I irrevocably WAIVE, RELEASE, and COVENANT NOT TO SUE Seutrade Financial Services, its officers, agents, and assigns on any claim in contract, TORT, or otherwise, and waive any right to assert liability of any kind against them." },
  { k: "no_misrepresentation", t: "No Misrepresentation", d: "I will not misrepresent Seutrade Financial Services, its products, trust structure, officers, or agents in any public or private communication." },
  { k: "non_solicitation", t: "Non-Solicitation", d: "I will not solicit, divert, poach, or interfere with the customers, borrowers, lenders, or vendors of Seutrade Financial Services during or after my engagement." },
  { k: "costs_and_fees", t: "Costs, Fees & Enforcement", d: "I shall be liable for all court costs, arbitration and administrative costs, collection costs, and the reasonable attorneys' fees incurred by Seutrade Financial Services in any dispute, enforcement, or breach." },
];

const DONTS = [
  "Do NOT submit non-mortgage debt or interest-bearing products through the broker desk.",
  "Do NOT alter, backdate, or fabricate borrower documents or signatures.",
  "Do NOT e-sign on the borrower's behalf — the borrower must e-sign their own closing binder.",
  "Do NOT disparage Seutrade Financial Services or share confidential deal data publicly.",
];

export default function BrokerPortal() {
  const [token, setToken] = useState(() => localStorage.getItem(TKEY) || null);
  const [me, setMe] = useState(undefined);
  const [view, setView] = useState("home");

  const authAxios = useMemo(() => axios.create({ baseURL: API, headers: token ? { Authorization: `Bearer ${token}` } : {} }), [token]);

  useEffect(() => {
    if (!token) { setMe(null); return; }
    authAxios.get("/broker/me").then((r) => { setMe(r.data); setView("dashboard"); })
      .catch(() => { localStorage.removeItem(TKEY); setToken(null); setMe(null); });
  }, [token]); // eslint-disable-line

  const onAuthed = (data) => {
    localStorage.setItem(TKEY, data.token);
    setToken(data.token);
    setMe({ ...data.user, profile: data.profile });
    setView("dashboard");
  };
  const logout = () => { localStorage.removeItem(TKEY); setToken(null); setMe(null); setView("home"); };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#ECECE4]" data-testid="broker-portal">
      <SealWatermark fixed size={720} opacity={0.04} />
      <header className="px-6 sm:px-10 py-5 flex items-center justify-between border-b border-[#1C1C16] sticky top-0 bg-[#0A0A0A]/95 backdrop-blur z-20">
        <button onClick={() => setView(me ? "dashboard" : "home")} className="text-left">
          <div className="font-serif text-[20px] tracking-tight leading-none">SEUTRADE FINANCIAL SERVICES</div>
          <div className="text-[9.5px] tracking-[0.24em] uppercase text-[#C9A227] mt-1">Broker Desk · Partner Program</div>
        </button>
        <div className="flex items-center gap-4">
          <a href="/" data-testid="broker-home-link" className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.14em] text-[#8A8A8A] hover:text-[#ECECE4]">← Home</a>
          {me ? (
            <>
              <span className="text-[12px] text-[#9A9A90]">{me.profile?.company || me.name}</span>
              <InboxBell client={authAxios} accent="#C9A227" />
              <button onClick={logout} data-testid="broker-logout" className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.14em] text-[#C9A227] hover:text-[#e0b62e]"><LogOut size={13} /> Sign out</button>
            </>
          ) : (
            <button onClick={() => setView("auth")} data-testid="broker-signin-nav" className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.14em] font-bold text-[#C9A227] hover:text-[#e0b62e]">Broker Sign in / Apply <ArrowRight size={13} /></button>
          )}
        </div>
      </header>

      {view === "home" && <Marketing onStart={() => setView("auth")} />}
      {view === "auth" && <AuthPanel onAuthed={onAuthed} />}
      {view === "dashboard" && me && <Dashboard authAxios={authAxios} me={me} refreshMe={() => authAxios.get("/broker/me").then((r) => setMe(r.data))} token={token} />}
    </div>
  );
}

const Marketing = ({ onStart }) => (
  <main>
    <section className="px-6 py-20 max-w-5xl mx-auto text-center">
      <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase text-[#8A8A8A] mb-6"><Handshake size={12} className="text-[#C9A227]" /> Partner Program · Instant Approval</div>
      <h1 className="font-serif text-4xl sm:text-6xl tracking-tight leading-[1.05]">Become a <span className="text-[#C9A227]">Seutrade Partner.</span><br />Place 0% refi mortgages.</h1>
      <p className="text-[#9A9A90] text-base mt-6 max-w-2xl mx-auto leading-relaxed">Mortgage brokers, loan officers, and <strong>real estate agents</strong> can partner with Seutrade Financial Services to place <strong>0% refinance mortgages</strong> — residential &amp; commercial. Sign up, onboard your clients, and track every deal from intake to closing. Earn a perpetual commission for the life of each note.</p>
      <button onClick={onStart} data-testid="broker-apply-cta" className="mt-9 inline-flex items-center gap-2 bg-[#C9A227] hover:bg-[#b8901f] text-[#0A0A0A] px-7 py-3 rounded-full text-[12px] uppercase tracking-[0.16em] font-bold transition-colors">Sign up &amp; submit deals <ArrowRight size={15} /></button>
    </section>
    <section className="px-6 pb-24 max-w-5xl mx-auto grid sm:grid-cols-3 gap-5">
      {[
        { icon: ClipboardCheck, t: "Standardized intake", d: "A guided checklist for every new client — ID, income, mortgage statement, and property details." },
        { icon: Truck, t: "Full pipeline visibility", d: "Track each deal through Intake → Approval → Disbursement → Electronic Pre-Closing." },
        { icon: Percent, t: "Perpetual commission", d: "2% of the monthly note in year 1, rising to 5% — paid for the life of the note." },
      ].map((c) => (
        <div key={c.t} className="bg-[#13130E] border border-[#2A2A22] rounded-lg p-7">
          <div className="w-11 h-11 rounded-md bg-[#C9A227]/10 border border-[#C9A227]/30 flex items-center justify-center mb-5"><c.icon size={18} className="text-[#C9A227]" /></div>
          <div className="font-serif text-xl mb-2">{c.t}</div>
          <p className="text-[#8A8A8A] text-sm leading-relaxed">{c.d}</p>
        </div>
      ))}
    </section>
  </main>
);

const AuthPanel = ({ onAuthed }) => {
  const [mode, setMode] = useState("register");
  const [form, setForm] = useState({ email: "", password: "", contact_name: "", company: "", applicant_type: "broker_firm", nmls_no: "", phone: "", license_no: "", ein: "", address: "" });
  const [acks, setAcks] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const up = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const allAcked = POLICIES.every((p) => acks[p.k]);

  const submit = async () => {
    setBusy(true); setError(null);
    try {
      if (mode === "register") {
        const r = await axios.post(`${API}/broker/register`, { ...form, policies_accepted: allAcked });
        onAuthed(r.data);
      } else {
        const r = await axios.post(`${API}/broker/login`, { email: form.email, password: form.password });
        onAuthed(r.data);
      }
    } catch (e) { setError(errMsg(e)); }
    setBusy(false);
  };

  return (
    <main className="px-6 py-12 flex justify-center">
      <div className="w-full max-w-2xl" data-testid="broker-auth-panel">
        <div className="flex gap-2 mb-6">
          {["register", "login"].map((m) => (
            <button key={m} onClick={() => { setMode(m); setError(null); }} data-testid={`broker-tab-${m}`}
              className={`flex-1 py-2 text-[11px] uppercase tracking-[0.14em] font-bold rounded-sm ${mode === m ? "bg-[#C9A227] text-[#0A0A0A]" : "bg-[#0F0F0F] text-[#8A8A8A] border border-[#2A2A22]"}`}>
              {m === "register" ? "Broker Application" : "Broker Sign In"}
            </button>
          ))}
        </div>

        <div className="bg-[#13130E] border border-[#2A2A22] rounded-lg p-7">
          {mode === "register" && (
            <>
              <div className="mb-1">
                <Label>I am applying as</Label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-1" data-testid="broker-applicant-type">
                  {[
                    { k: "broker_firm", t: "Mortgage Broker / Firm" },
                    { k: "loan_officer", t: "Loan Officer" },
                    { k: "real_estate_agent", t: "Real Estate Agent" },
                    { k: "referral_agent", t: "Referral Agent" },
                  ].map((o) => (
                    <button key={o.k} type="button" onClick={() => up("applicant_type", o.k)}
                      data-testid={`broker-type-${o.k}`}
                      className={`px-4 py-2.5 rounded-md text-[12px] font-bold uppercase tracking-[0.1em] border transition-colors ${form.applicant_type === o.k ? "bg-[#C9A227] text-[#0A0A0A] border-[#C9A227]" : "bg-[#0F0F0F] text-[#C9C9C0] border-[#2A2A22] hover:border-[#C9A227]/60"}`}>
                      {o.t}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label>{form.applicant_type === "loan_officer" ? "Firm you originate under" : "Company / firm"}</Label><Inp v={form.company} set={(v) => up("company", v)} testid="broker-company" /></div>
                <div><Label>{form.applicant_type === "loan_officer" ? "Loan officer name" : "Contact name"}</Label><Inp v={form.contact_name} set={(v) => up("contact_name", v)} testid="broker-contact" /></div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label>Phone</Label><Inp v={form.phone} set={(v) => up("phone", v)} testid="broker-phone" /></div>
                <div><Label>NMLS ID{form.applicant_type === "loan_officer" ? " (individual)" : ""}</Label><Inp v={form.nmls_no} set={(v) => up("nmls_no", v)} testid="broker-nmls" /></div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label>State license no.</Label><Inp v={form.license_no} set={(v) => up("license_no", v)} testid="broker-license" /></div>
                <div><Label>EIN (optional)</Label><Inp v={form.ein} set={(v) => up("ein", v)} testid="broker-ein" /></div>
              </div>
              <div className="grid grid-cols-1 gap-3">
                <div><Label>Business address</Label><Inp v={form.address} set={(v) => up("address", v)} testid="broker-address" /></div>
              </div>
            </>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label>Email</Label><Inp v={form.email} set={(v) => up("email", v)} testid="broker-email" type="email" /></div>
            <div><Label>Password {mode === "register" ? "(min 8)" : ""}</Label><Inp v={form.password} set={(v) => up("password", v)} testid="broker-password" type="password" /></div>
          </div>

          {mode === "register" && (
            <div className="mt-5 border-t border-[#2A2A22] pt-5" data-testid="broker-policies">
              <div className="flex items-center gap-2 mb-1"><ShieldCheck size={15} className="text-[#C9A227]" /><span className="font-serif text-[15px]">Seutrade Broker Policies — Do's</span></div>
              <p className="text-[11px] text-[#8A8A8A] mb-3">You must read and accept each policy. Violation of any policy may result in <strong className="text-[#FF8585]">immediate suspension or termination</strong> of your broker account and forfeiture of pending commissions.</p>
              <div className="space-y-2.5">
                {POLICIES.map((p) => (
                  <label key={p.k} className="flex items-start gap-2.5 cursor-pointer" data-testid={`broker-policy-${p.k}`}>
                    <input type="checkbox" checked={!!acks[p.k]} onChange={(e) => setAcks((a) => ({ ...a, [p.k]: e.target.checked }))}
                      data-testid={`broker-policy-check-${p.k}`} className="mt-0.5 accent-[#C9A227] w-4 h-4 shrink-0" />
                    <span className="text-[12px] leading-relaxed text-[#C9C9C0]"><strong className="text-[#ECECE4]">{p.t}.</strong> {p.d}</span>
                  </label>
                ))}
              </div>
              <div className="mt-4 bg-[#9E2A2B]/10 border border-[#9E2A2B]/40 rounded-md p-3">
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.12em] font-bold text-[#FF8585] mb-1.5"><Ban size={13} /> Prohibited — Don'ts</div>
                <ul className="text-[11.5px] text-[#E9B4B4] list-disc pl-5 space-y-1">
                  {DONTS.map((d) => <li key={d}>{d}</li>)}
                </ul>
              </div>
            </div>
          )}

          <button onClick={submit} disabled={busy || (mode === "register" && (!allAcked || !form.company || !form.contact_name))} data-testid="broker-auth-submit"
            className="w-full mt-6 bg-[#C9A227] hover:bg-[#b8901f] disabled:opacity-40 disabled:cursor-not-allowed text-[#0A0A0A] px-4 py-2.5 text-[11px] uppercase tracking-[0.16em] font-bold rounded-sm flex items-center justify-center gap-2">
            {busy ? <Loader2 size={14} className="animate-spin" /> : null}{mode === "register" ? "Submit Broker Application" : "Sign In"}
          </button>
          {mode === "register" && !allAcked && <div className="text-[11px] text-[#8A8A8A] mt-2 text-center">Accept all policies above to enable submission.</div>}
          {mode === "login" && (
            <div className="text-center mt-3">
              <a href="/broker/forgot-password" data-testid="broker-forgot-link" className="text-[11px] text-[#8A8A8A] hover:text-[#E4C97A] transition-colors underline">Forgot password?</a>
            </div>
          )}
          {error && <div className="text-[11px] text-[#FF8585] mt-3" data-testid="broker-auth-error">✗ {error}</div>}
        </div>
      </div>
    </main>
  );
};

const STATUS_BANNER = {
  pending: { icon: Clock, cls: "bg-[#B8860B]/10 border-[#B8860B]/40 text-[#E4C97A]", t: "Application under review", d: "Your broker application has been submitted to Seutrade Financial Services. You'll be able to onboard clients once an admin approves your account." },
  rejected: { icon: XCircle, cls: "bg-[#9E2A2B]/15 border-[#9E2A2B]/50 text-[#FF8585]", t: "Application not approved", d: "Your broker application was not approved. Please contact the desk for details." },
  suspended: { icon: AlertTriangle, cls: "bg-[#9E2A2B]/15 border-[#9E2A2B]/50 text-[#FF8585]", t: "Account suspended", d: "Your broker account is suspended pending policy review. Client onboarding is disabled." },
  terminated: { icon: Ban, cls: "bg-[#9E2A2B]/20 border-[#9E2A2B]/60 text-[#FF8585]", t: "Account terminated", d: "Your broker account has been terminated for cause. Client onboarding is permanently disabled. Contact the desk with questions." },
};

const Dashboard = ({ authAxios, me, refreshMe, token }) => {
  const [clients, setClients] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const rawStatus = me.profile?.status;
  const isAdmin = me.role === "admin";
  const status = isAdmin ? "approved" : ((rawStatus == null || rawStatus === "") ? "pending" : String(rawStatus).trim().toLowerCase());
  // Approved (and the equivalent "active") brokers can onboard clients. Admins
  // always have full access. Tolerant of casing/whitespace/legacy values so an
  // approved broker is never wrongly shown the "under review" gate.
  const isApproved = isAdmin || status === "approved" || status === "active";
  const reload = () => {
    authAxios.get("/broker/clients").then((r) => setClients(r.data.clients)).catch(() => {});
    authAxios.get("/broker/receipts").then((r) => setReceipts(r.data.receipts || [])).catch(() => {});
  };
  useEffect(() => { if (isApproved) reload(); }, [isApproved]); // eslint-disable-line

  // Live refresh: pick up desk/admin edits (status, binder, payments) automatically.
  useEffect(() => {
    if (!isApproved) return;
    const onVisible = () => { if (document.visibilityState === "visible") reload(); };
    window.addEventListener("focus", reload);
    document.addEventListener("visibilitychange", onVisible);
    const iv = setInterval(() => { if (document.visibilityState === "visible") reload(); }, 45000);
    return () => { window.removeEventListener("focus", reload); document.removeEventListener("visibilitychange", onVisible); clearInterval(iv); };
  }, [isApproved]); // eslint-disable-line

  // Finalize Stripe-hosted broker-initiated payment flows on return
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const app = p.get("app");
    const clean = () => window.history.replaceState({}, "", window.location.pathname);
    const setup = p.get("bkr_setup"), closing = p.get("bkr_closing"), inst = p.get("bkr_installment");
    if (app && setup) {
      authAxios.get(`/broker/clients/${app}/payment-setup/status/${setup}`)
        .then((r) => { if (r.data.enrolled) toast.success("Borrower's payment method saved on file."); reload(); })
        .catch((e) => toast.error(errMsg(e))).finally(clean);
    } else if (app && closing) {
      authAxios.get(`/broker/clients/${app}/pay-closing-fees/status/${closing}`)
        .then((r) => { if (r.data.paid) toast.success("Closing costs paid for the borrower."); reload(); })
        .catch((e) => toast.error(errMsg(e))).finally(clean);
    } else if (app && inst) {
      authAxios.get(`/broker/clients/${app}/pay-installment/status/${inst}`)
        .then((r) => { if (r.data.paid) toast.success("Monthly payment processed for the borrower."); reload(); })
        .catch((e) => toast.error(errMsg(e))).finally(clean);
    } else if (p.get("bkr_setup_cancelled") || p.get("bkr_closing_cancelled") || p.get("bkr_installment_cancelled")) {
      clean();
    }
  }, []); // eslint-disable-line

  const comm = me.commission_structure;
  const isReferral = me.profile?.applicant_type === "referral_agent";

  return (
    <main className="px-6 py-10 max-w-5xl mx-auto space-y-10" data-testid="broker-dashboard">
      <a href="/securities" data-testid="broker-securities-tile"
        className="flex items-center gap-4 rounded-lg border border-[#B8860B]/40 bg-gradient-to-r from-[#1A1606] to-[#13130E] px-5 py-4 hover:border-[#E4C97A] transition-colors group">
        <span className="w-11 h-11 rounded-md bg-[#C9A227]/10 border border-[#C9A227]/30 flex items-center justify-center shrink-0"><Landmark size={20} className="text-[#C9A227]" /></span>
        <span className="flex-1">
          <span className="block text-[15px] font-serif text-[#E4C97A]">Open a SEUTRADE Securities Account</span>
          <span className="block text-[12px] text-[#9A9A90] mt-0.5">Letter of Credit · SBLC · Surety Bond · Proof of Funds — with a USD collateral ledger you can lend against. Fees just 0.5%.</span>
        </span>
        <ArrowRight size={16} className="text-[#C9A227] group-hover:translate-x-1 transition-transform" />
      </a>
      <a href="/tax-debt-program" data-testid="broker-tax-program-tile"
        className="flex items-center gap-4 rounded-lg border border-[#14503B] bg-gradient-to-r from-[#06160F] to-[#0A241A] px-5 py-4 hover:border-[#6FD3AE] transition-colors group">
        <span className="w-11 h-11 rounded-md bg-[#6FD3AE]/10 border border-[#14503B] flex items-center justify-center shrink-0"><Landmark size={20} className="text-[#6FD3AE]" /></span>
        <span className="flex-1">
          <span className="block text-[15px] font-serif text-[#6FD3AE]">Tax Debt Assistance Program</span>
          <span className="block text-[12px] text-[#9A9A90] mt-0.5">Enroll a client to settle IRS tax debt with a transferred tax credit and release liens, levies &amp; garnishments.</span>
        </span>
        <span data-testid="broker-tax-program-signup" className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.12em] font-bold text-[#06160F] bg-[#6FD3AE] px-3.5 py-2 rounded-full">Sign Up <ArrowRight size={13} /></span>
      </a>
      <a href="/certificate?scope=member" data-testid="broker-certificate-link"
        className="flex items-center gap-4 rounded-lg border border-[#8A6D1F]/50 bg-gradient-to-r from-[#17120a] to-[#1f1708] px-5 py-4 hover:border-[#E4C97A] transition-colors">
        <span className="w-11 h-11 rounded-md bg-[#B8860B]/10 border border-[#8A6D1F]/50 flex items-center justify-center shrink-0"><ShieldCheck size={20} className="text-[#E4C97A]" /></span>
        <span className="flex-1">
          <span className="block text-[15px] font-serif text-[#E4C97A]">Your Certificate of Authority</span>
          <span className="block text-[12px] text-[#9A9A90] mt-0.5">Member certificate — membership in SEUTRADE (a § 508(c)(1)(A) faith-based organization) — printable &amp; eFaxable.</span>
        </span>
        <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.12em] font-bold text-[#0B0B07] bg-[#E4C97A] px-3.5 py-2 rounded-full">View <ArrowRight size={13} /></span>
      </a>
      <div>
        <h2 className="font-serif text-2xl">Broker Desk — {me.profile?.company || me.name}</h2>
        <div className="text-[12px] text-[#8A8A8A] mt-1">Status: <StatusPill status={status} /></div>
      </div>

      {!isApproved ? (
        <Banner {...(STATUS_BANNER[status] || STATUS_BANNER.pending)} note={me.profile?.review_note} />
      ) : (
        <>
          {isReferral ? <ReferralFeeCard /> : <CommissionCard comm={comm} />}
          <EarningsLedger authAxios={authAxios} clients={clients} />
          <CommissionStatement authAxios={authAxios} clients={clients} />
          <BrokerConnectCard authAxios={authAxios} />
          <BrokerAccountSettings authAxios={authAxios} me={me} onSaved={refreshMe} />
          <BrokerLinks authAxios={authAxios} onChange={reload} />
          <ClientIntake authAxios={authAxios} onSubmitted={reload} />
          <ClientPipeline clients={clients} authAxios={authAxios} token={token} onChange={reload} />
          <BrokerReceipts receipts={receipts} />
        </>
      )}
      <SupportMessaging client={authAxios} accent="#B8860B" />
    </main>
  );
};

const CommissionStatement = ({ authAxios, clients }) => {
  const now = new Date();
  const [month, setMonth] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    let ok = true;
    setLoading(true);
    authAxios.get(`/broker/commission-statement?month=${month}`)
      .then((r) => { if (ok) setData(r.data); })
      .catch((e) => { if (ok) toast.error(e?.response?.data?.detail || "Could not load statement"); })
      .finally(() => { if (ok) setLoading(false); });
    return () => { ok = false; };
  }, [authAxios, month, clients]);

  const printStatement = () => {
    if (!data) return;
    const row = (cells) => `<tr>${cells.map((c) => `<td style="padding:6px 10px;border-bottom:1px solid #e5e5e5;font-size:12px">${c}</td>`).join("")}</tr>`;
    const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    const earnedRows = (data.earned || []).map((e) => row([e.client, `${e.rate != null ? e.rate + "%" : "—"}`, money(e.monthly_commission)])).join("") || row(["—", "", ""]);
    const paidRows = (data.paid || []).map((p) => row([p.date, p.client, p.status, money(p.amount), p.ref || "—"])).join("") || row(["—", "", "", "", ""]);
    const cancRows = (data.cancelled || []).map((c) => row([c.client, c.cancelled_at ? String(c.cancelled_at).slice(0, 10) : "—", money(c.forfeited)])).join("") || row(["—", "", ""]);
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Commission Statement — ${data.period_label}</title></head>
    <body style="font-family:Georgia,serif;color:#1a1a1a;max-width:800px;margin:24px auto;padding:0 16px">
      <div style="border-bottom:3px solid #0e2a6b;padding-bottom:10px;margin-bottom:16px">
        <div style="font-size:22px;font-weight:800;letter-spacing:.04em;color:#0e2a6b">SEUTRADE FINANCIAL SERVICES</div>
        <div style="font-size:13px;color:#555">Broker Commission Statement</div>
      </div>
      <table style="width:100%;font-size:12px;margin-bottom:14px"><tr>
        <td><strong>Broker:</strong> ${data.broker.name}${data.broker.company ? " · " + data.broker.company : ""}<br><strong>Email:</strong> ${data.broker.email}</td>
        <td style="text-align:right"><strong>Period:</strong> ${data.period_label}<br><strong>Generated:</strong> ${new Date(data.generated_at).toLocaleString()}</td>
      </tr></table>
      <div style="display:flex;gap:10px;margin-bottom:18px">
        <div style="flex:1;border:1px solid #ddd;border-radius:6px;padding:8px;text-align:center"><div style="font-size:10px;text-transform:uppercase;color:#777">Earned (mo.)</div><div style="font-size:16px;font-weight:800;color:#0b6b4f">${money(data.earned_total)}</div></div>
        <div style="flex:1;border:1px solid #ddd;border-radius:6px;padding:8px;text-align:center"><div style="font-size:10px;text-transform:uppercase;color:#777">Paid</div><div style="font-size:16px;font-weight:800">${money(data.paid_total)}</div></div>
        <div style="flex:1;border:1px solid #ddd;border-radius:6px;padding:8px;text-align:center"><div style="font-size:10px;text-transform:uppercase;color:#777">Cancelled</div><div style="font-size:16px;font-weight:800;color:#b8101a">${money(data.cancelled_total)}</div></div>
        <div style="flex:1;border:1px solid #ddd;border-radius:6px;padding:8px;text-align:center"><div style="font-size:10px;text-transform:uppercase;color:#777">Accrued (unpaid)</div><div style="font-size:16px;font-weight:800">${money(data.accrued_unpaid_total)}</div></div>
      </div>
      <h3 style="font-size:13px;border-bottom:1px solid #0e2a6b;padding-bottom:4px">Commission Earned This Month (active notes)</h3>
      <table style="width:100%;border-collapse:collapse"><thead><tr><th style="text-align:left;font-size:10px;text-transform:uppercase;color:#777;padding:4px 10px">Client</th><th style="text-align:left;font-size:10px;text-transform:uppercase;color:#777;padding:4px 10px">Rate</th><th style="text-align:left;font-size:10px;text-transform:uppercase;color:#777;padding:4px 10px">Monthly Commission</th></tr></thead><tbody>${earnedRows}</tbody></table>
      <h3 style="font-size:13px;border-bottom:1px solid #0e2a6b;padding-bottom:4px;margin-top:16px">Payments Received This Month</h3>
      <table style="width:100%;border-collapse:collapse"><thead><tr><th style="text-align:left;font-size:10px;text-transform:uppercase;color:#777;padding:4px 10px">Date</th><th style="text-align:left;font-size:10px;text-transform:uppercase;color:#777;padding:4px 10px">Client</th><th style="text-align:left;font-size:10px;text-transform:uppercase;color:#777;padding:4px 10px">Status</th><th style="text-align:left;font-size:10px;text-transform:uppercase;color:#777;padding:4px 10px">Amount</th><th style="text-align:left;font-size:10px;text-transform:uppercase;color:#777;padding:4px 10px">Ref</th></tr></thead><tbody>${paidRows}</tbody></table>
      <h3 style="font-size:13px;border-bottom:1px solid #0e2a6b;padding-bottom:4px;margin-top:16px">Cancelled / Forfeited</h3>
      <table style="width:100%;border-collapse:collapse"><thead><tr><th style="text-align:left;font-size:10px;text-transform:uppercase;color:#777;padding:4px 10px">Client</th><th style="text-align:left;font-size:10px;text-transform:uppercase;color:#777;padding:4px 10px">Cancelled</th><th style="text-align:left;font-size:10px;text-transform:uppercase;color:#777;padding:4px 10px">Forfeited</th></tr></thead><tbody>${cancRows}</tbody></table>
      <p style="font-size:10px;color:#888;margin-top:20px">This statement is generated for informational purposes. Commission accrues for the life of each note per the published schedule and is paid as funds clear. Internal — unaudited.</p>
    </body></html>`;
    const w = window.open("", "_blank");
    if (w) { w.document.write(html); w.document.close(); w.focus(); setTimeout(() => w.print(), 400); }
  };

  return (
    <div className="bg-[#13130E] border border-[#C9A227]/30 rounded-lg p-6 mt-6" data-testid="broker-commission-statement">
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <Wallet2Icon /><span className="font-serif text-lg">Monthly Commission Statement</span>
        <div className="ml-auto flex items-center gap-2">
          <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="statement-month"
            className="bg-[#0F0F0F] border border-[#2A2A22] rounded-md px-3 py-1.5 text-[12px] text-[#ECECE4]" />
          <button onClick={printStatement} disabled={!data || loading} className="inline-flex items-center gap-1.5 rounded-md bg-[#C8A24B] hover:bg-[#d8b25b] text-black text-[12px] font-semibold px-3 py-1.5 disabled:opacity-50" data-testid="statement-print-btn">Download / Print</button>
        </div>
      </div>
      {loading ? (
        <p className="text-[12px] text-[#8A8A8A]">Loading…</p>
      ) : !data ? (
        <p className="text-[12px] text-[#8A8A8A]">Select a month to generate your statement.</p>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <LedgerStat label="Earned this month" value={fmt(data.earned_total)} accent testid="stmt-earned" />
            <LedgerStat label="Paid this month" value={fmt(data.paid_total)} testid="stmt-paid" />
            <LedgerStat label="Cancelled" value={fmt(data.cancelled_total)} testid="stmt-cancelled" />
            <LedgerStat label="Accrued (unpaid)" value={fmt(data.accrued_unpaid_total)} testid="stmt-accrued" />
          </div>
          <div className="text-[11px] text-[#8A8A8A] mb-3">{data.period_label} · {(data.earned || []).length} active note(s) accruing · {(data.paid || []).length} payment(s) received</div>
          {(data.paid || []).length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-[12px]" data-testid="statement-paid-table">
                <thead><tr className="text-[10px] uppercase tracking-[0.12em] text-[#8A8A8A] border-b border-[#2A2A22]">
                  <th className="text-left py-2 pr-3">Date</th><th className="text-left py-2 pr-3">Client</th><th className="text-right py-2 pr-3">Amount</th><th className="text-left py-2">Ref</th>
                </tr></thead>
                <tbody>
                  {data.paid.map((p, i) => (
                    <tr key={i} className="border-b border-[#1F1F19]">
                      <td className="py-2 pr-3 text-[#C9C9C0]">{p.date}</td>
                      <td className="py-2 pr-3 text-[#ECECE4]">{p.client}</td>
                      <td className="py-2 pr-3 text-right text-[#E4C97A] font-semibold">{fmt(p.amount)}</td>
                      <td className="py-2 text-[#9A9A90] text-[10px]">{p.ref || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {(data.cancelled || []).length > 0 && (
            <div className="mt-3 text-[11px] text-[#FF8585]" data-testid="statement-cancelled">
              Cancelled / forfeited: {data.cancelled.map((c) => `${c.client} (${fmt(c.forfeited)})`).join(", ")}
            </div>
          )}
        </>
      )}
    </div>
  );
};

const EarningsLedger = ({ authAxios, clients }) => {
  const [data, setData] = useState(null);
  const load = React.useCallback(async () => {
    try {
      const r = await authAxios.get("/broker/earnings");
      setData(r.data);
    } catch { /* ignore */ }
  }, [authAxios]);
  useEffect(() => { load(); }, [load, clients]);
  const rows = data?.rows || [];
  return (
    <div className="bg-[#13130E] border border-[#C9A227]/30 rounded-lg p-6" data-testid="broker-earnings-ledger">
      <div className="flex items-center gap-2 mb-4"><Wallet2Icon /><span className="font-serif text-lg">Your Payout Ledger</span></div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        <LedgerStat label="Commission paid" value={fmt(data?.total_commission || 0)} testid="ledger-total-commission" />
        <LedgerStat label="Admin fees paid" value={fmt(data?.total_admin_fee || 0)} testid="ledger-total-adminfee" />
        <LedgerStat label="Total received" value={fmt(data?.total_paid || 0)} accent testid="ledger-total-paid" />
        <LedgerStat label="Accrued (unpaid)" value={fmt(data?.accrued_unpaid || 0)} testid="ledger-accrued" />
      </div>
      {rows.length === 0 ? (
        <p className="text-[12px] text-[#8A8A8A]" data-testid="ledger-empty">No payouts yet. Your commissions and one-time administrative / referral fees will appear here — each with its Stripe transfer reference — as deals close and funds clear.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[12px]" data-testid="ledger-table">
            <thead>
              <tr className="text-[10px] uppercase tracking-[0.12em] text-[#8A8A8A] border-b border-[#2A2A22]">
                <th className="text-left py-2 pr-3">Date</th><th className="text-left py-2 pr-3">Client</th>
                <th className="text-left py-2 pr-3">Type</th><th className="text-right py-2 pr-3">Amount</th>
                <th className="text-left py-2 pr-3">Status</th><th className="text-left py-2">Stripe ref</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-b border-[#1F1F19]" data-testid={`ledger-row-${i}`}>
                  <td className="py-2 pr-3 text-[#C9C9C0]">{r.date ? new Date(r.date).toLocaleDateString() : "—"}</td>
                  <td className="py-2 pr-3 text-[#ECECE4]">{r.client}</td>
                  <td className="py-2 pr-3"><span className={`px-2 py-0.5 rounded-full text-[10px] ${r.type === "admin_fee" ? "bg-[#C8A24B]/12 text-[#E4C97A] border border-[#C8A24B]/40" : "bg-[#0B6B4F]/15 text-[#7DD3B8] border border-[#0B6B4F]/50"}`}>{r.label || r.type}</span></td>
                  <td className="py-2 pr-3 text-right text-[#E4C97A] font-semibold">{fmt(r.amount)}</td>
                  <td className="py-2 pr-3 text-[#9A9A90]">{r.status === "transferred" ? <span className="text-[#3DBB8E]">transferred</span> : r.status}</td>
                  <td className="py-2 text-[10px] text-[#6E6E64] font-mono">{r.stripe_transfer_id || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const LedgerStat = ({ label, value, accent, testid }) => (
  <div className={`rounded-md p-3 border ${accent ? "border-[#C9A227]/50 bg-[#C9A227]/8" : "border-[#2A2A22] bg-[#0F0F0F]"}`}>
    <div className="text-[10px] uppercase tracking-[0.12em] text-[#8A8A8A]">{label}</div>
    <div className={`text-lg font-serif mt-1 ${accent ? "text-[#E4C97A]" : "text-[#ECECE4]"}`} data-testid={testid}>{value}</div>
  </div>
);

const Wallet2Icon = () => <CreditCard size={16} className="text-[#C9A227]" />;

const ReferralFeeCard = () => (
  <div className="bg-[#13130E] border border-[#C9A227]/30 rounded-lg p-6" data-testid="broker-referral-fee-card">
    <div className="flex items-center gap-2 mb-3"><Percent size={16} className="text-[#C9A227]" /><span className="font-serif text-lg">Your Referral Fee — Flat, Per Closed Deal</span></div>
    <div className="text-[28px] font-serif text-[#E4C97A]">$150.00</div>
    <p className="text-[12px] text-[#C9C9C0] mt-2">You earn a one-time <span className="text-[#C9A227] font-semibold">$150 referral fee</span> for each client you refer whose loan closes. As a Referral Agent you do not earn the perpetual monthly note commission.</p>
    <p className="text-[11px] text-[#8A8A8A] mt-2 leading-relaxed">The $150 is paid to your connected Stripe payout account automatically, once the borrower's closing costs clear and an admin approves the payout — each with its own Stripe transfer reference in your ledger below.</p>
  </div>
);

const CommissionCard = ({ comm }) => (
  <div className="bg-[#13130E] border border-[#C9A227]/30 rounded-lg p-6" data-testid="broker-commission-card">
    <div className="flex items-center gap-2 mb-3"><Percent size={16} className="text-[#C9A227]" /><span className="font-serif text-lg">Your Commission — Perpetual, Life of Note</span></div>
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      {(comm?.tiers || []).map((t) => (
        <div key={t.year} className="bg-[#0F0F0F] border border-[#2A2A22] rounded-md p-4 text-center">
          <div className="text-[10px] uppercase tracking-[0.12em] text-[#8A8A8A]">Year {t.year}</div>
          <div className="text-2xl font-serif text-[#C9A227] mt-1">{Math.round(t.rate * 100)}%</div>
          <div className="text-[10px] text-[#5C5C5C]">of monthly note</div>
        </div>
      ))}
    </div>
    <p className="text-[11px] text-[#8A8A8A] mt-3 leading-relaxed">{comm?.note}</p>
  </div>
);

const BrokerAcctInput = ({ label, val, on, type = "text", testid }) => (
  <div>
    <label className="text-[10px] uppercase tracking-[0.16em] text-[#8A8A8A] block mb-1">{label}</label>
    <input type={type} value={val} onChange={(e) => on(e.target.value)} data-testid={testid}
      className="w-full bg-[#0A0A0A] border border-[#2A2A22] rounded-sm px-3 py-2 text-[#ECECE4] focus:border-[#B8860B] focus:outline-none" />
  </div>
);

const BrokerAccountSettings = ({ authAxios, me, onSaved }) => {
  const p = me.profile || {};
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ contact_name: me.name || p.contact_name || "", company: p.company || "", phone: p.phone || "", address: p.address || "", nmls_no: p.nmls_no || "", license_no: p.license_no || "", ein: p.ein || "" });
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const set = (k) => (v) => setForm((s) => ({ ...s, [k]: v }));
  const save = async () => {
    setSaving(true); setMsg(null);
    const body = { ...form };
    if (pw.new_password) { body.current_password = pw.current_password; body.new_password = pw.new_password; }
    try {
      await authAxios.put("/broker/me", body);
      setMsg({ ok: true, t: "Account saved." });
      setPw({ current_password: "", new_password: "" });
      onSaved && onSaved();
      setOpen(false);
    } catch (e) { setMsg({ ok: false, t: e?.response?.data?.detail || "Could not save." }); }
    setSaving(false);
  };
  return (
    <div className="rounded-lg border border-[#2A2A22] bg-[#13130E] p-6" data-testid="broker-account-settings">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-serif text-lg">My Account</h3>
          <p className="text-[12px] text-[#8A8A8A] mt-0.5">{me.name} · {me.email}</p>
        </div>
        <button onClick={() => setOpen((o) => !o)} data-testid="broker-account-edit-toggle"
          className="text-[11px] uppercase tracking-[0.12em] font-bold text-[#E4C97A] border border-[#B8860B]/40 hover:border-[#B8860B] px-4 py-2 rounded-sm">{open ? "Cancel" : "Edit"}</button>
      </div>
      {open && (
        <div className="mt-5 space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <BrokerAcctInput label="Contact Name" val={form.contact_name} on={set("contact_name")} testid="broker-account-contact_name" />
            <BrokerAcctInput label="Company" val={form.company} on={set("company")} testid="broker-account-company" />
            <BrokerAcctInput label="Phone" val={form.phone} on={set("phone")} testid="broker-account-phone" />
            <BrokerAcctInput label="EIN" val={form.ein} on={set("ein")} testid="broker-account-ein" />
            <BrokerAcctInput label="NMLS #" val={form.nmls_no} on={set("nmls_no")} testid="broker-account-nmls_no" />
            <BrokerAcctInput label="License #" val={form.license_no} on={set("license_no")} testid="broker-account-license_no" />
          </div>
          <BrokerAcctInput label="Address" val={form.address} on={set("address")} testid="broker-account-address" />
          <div className="pt-3 border-t border-[#2A2A22]">
            <p className="text-[11px] uppercase tracking-[0.14em] text-[#8A8A8A] mb-2">Change Password (optional)</p>
            <div className="grid sm:grid-cols-2 gap-3">
              <BrokerAcctInput label="Current Password" type="password" val={pw.current_password} on={(v) => setPw((s) => ({ ...s, current_password: v }))} testid="broker-account-curpw" />
              <BrokerAcctInput label="New Password (min 8)" type="password" val={pw.new_password} on={(v) => setPw((s) => ({ ...s, new_password: v }))} testid="broker-account-newpw" />
            </div>
          </div>
          <button onClick={save} disabled={saving} data-testid="broker-account-save"
            className="w-full bg-[#B8860B] hover:bg-[#a0760a] disabled:opacity-50 text-[#0A0A0A] font-bold uppercase tracking-[0.14em] text-[12px] py-2.5 rounded-sm flex items-center justify-center gap-2">
            {saving ? <Loader2 size={14} className="animate-spin" /> : null} Save Changes
          </button>
        </div>
      )}
      {msg && <p className={`mt-3 text-[12px] ${msg.ok ? "text-[#3DBB8E]" : "text-[#FF8585]"}`} data-testid="broker-account-msg">{msg.t}</p>}
    </div>
  );
};


const BrokerConnectCard = ({ authAxios }) => {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const refresh = async () => {
    try { const { data } = await authAxios.get("/broker/connect/status"); setStatus(data); }
    catch (e) { /* ignore */ }
  };
  useEffect(() => { refresh(); }, []); // eslint-disable-line
  const onboard = async () => {
    setBusy(true); setNote("");
    try {
      const { data } = await authAxios.post("/broker/connect/onboard", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setNote(typeof detail === "string" ? detail : "Automatic payouts aren't available right now. Please try again later.");
      setBusy(false);
    }
  };
  const ready = status?.payouts_enabled;
  return (
    <div className="bg-[#13130E] border border-[#2A2A22] rounded-lg p-6" data-testid="broker-connect-card">
      <div className="flex items-center gap-2 mb-3">
        <Landmark size={16} className="text-[#635BFF]" />
        <span className="font-serif text-lg">Automatic Commission Payouts</span>
        {ready && <span className="text-[10px] uppercase tracking-[0.12em] text-[#3DBB8E] border border-[#0B6B4F]/50 rounded-full px-2.5 py-1" data-testid="broker-connect-ready-badge">Connected</span>}
      </div>
      {ready ? (
        <p className="text-[13px] text-[#3DBB8E]" data-testid="broker-connect-ready">✓ Your Stripe payout account is connected. Your monthly commissions are transferred to you automatically.</p>
      ) : (
        <>
          <p className="text-[13px] text-[#9A9A90] mb-4">Connect a Stripe payout account to receive your commissions automatically. You'll securely enter your identity and bank details on Stripe — we never see them.{status?.details_submitted && !ready ? " Your details are under review by Stripe." : ""}</p>
          <button onClick={onboard} disabled={busy} data-testid="broker-connect-btn" className="inline-flex items-center gap-2 bg-[#635BFF] hover:bg-[#5249e0] text-white px-6 py-3 rounded-md text-[12px] uppercase tracking-[0.14em] font-bold disabled:opacity-50">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Landmark size={15} />} {status?.connected ? "Continue Stripe onboarding" : "Connect payouts with Stripe"}
          </button>
          {note && <p className="mt-3 text-[12px] text-[#E4C97A]" data-testid="broker-connect-note">{note}</p>}
        </>
      )}
    </div>
  );
};


const CHECKLIST_ITEMS = [
  { k: "id_verified", t: "Government-issued photo ID verified" },
  { k: "income_docs", t: "Income / employment documentation collected" },
  { k: "mortgage_statement", t: "Current mortgage statement / payoff obtained" },
  { k: "property_docs", t: "Property deed / title reviewed" },
  { k: "borrower_consent", t: "Borrower consent to submit application obtained" },
];

const ClientIntake = ({ authAxios, onSubmitted }) => {
  const [f, setF] = useState({
    borrower_full_name: "", borrower_email: "", borrower_phone: "", borrower_address: "", borrower_ssn: "",
    borrower_dob: "", co_borrower_name: "", co_borrower_email: "", co_borrower_phone: "", co_borrower_address: "", co_borrower_ssn: "", co_borrower_dob: "", employer: "", annual_income: "",
    property_or_collateral: "", property_state: "", property_county: "", legal_description: "", tax_parcel_id: "", note_structure: "", property_use: "residential", occupancy: "owner_occupied",
    current_lender: "", current_lender_address: "", account_number: "",
    counterparty_bank_name: "", counterparty_bank_aba: "", counterparty_bank_account: "",
    current_balance: "", current_rate: "", current_payment: "", term_months: 360, notes: "",
    transaction_type: "refinance", debt_type: "mortgage", mortgage_position: "first",
    vehicle_vin: "", vehicle_year: "", vehicle_make: "", vehicle_model: "", vehicle_trim: "",
    vehicle_value: "", vehicle_purchase_price: "", vehicle_title_state: "", vehicle_title_status: "clean",
    assumption_existing_rate: "", assumption_monthly_payment: "", assumption_remaining_term: "",
    assumption_next_due_date: "", relocation_incentive: 500, vacate_date: "", deed_type: "warranty",
    trust_name: "",
    additional_debts: [],
    borrower_esignature_name: "", esign_attestation: "", esign_authorized: false, co_borrower_esignature_name: "",
  });
  const [checklist, setChecklist] = useState({});
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const up = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const isConsolidation = f.transaction_type === "consolidation";
  const isAssumption = f.transaction_type === "assumption";
  const isMortgage = f.debt_type === "mortgage";
  const isAuto = f.debt_type === "auto";
  const addDebt = () => setF((s) => ({ ...s, additional_debts: [...s.additional_debts, { current_lender: "", current_lender_address: "", account_number: "", current_balance: "", debt_type: "mortgage" }] }));
  const rmDebt = (i) => setF((s) => ({ ...s, additional_debts: s.additional_debts.filter((_, x) => x !== i) }));
  const upDebt = (i, k, v) => setF((s) => ({ ...s, additional_debts: s.additional_debts.map((d, x) => x === i ? { ...d, [k]: v } : d) }));
  const allChecked = CHECKLIST_ITEMS.every((c) => checklist[c.k]);

  const submit = async () => {
    setBusy(true); setMsg(null);
    try {
      const r = await authAxios.post("/broker/clients", {
        ...f, checklist,
        origin_url: window.location.origin,
        borrower_esignature_name: f.esign_authorized ? (f.borrower_esignature_name || "").trim() : "",
        esign_attestation: f.esign_authorized ? (f.esign_attestation || "").trim() : "",
        co_borrower_esignature_name: f.esign_authorized ? (f.co_borrower_esignature_name || "").trim() : "",
        current_balance: Number((isAuto && !f.current_balance ? f.vehicle_purchase_price : f.current_balance) || 0),
        current_rate: f.current_rate ? Number(f.current_rate) : null,
        current_payment: f.current_payment ? Number(f.current_payment) : null,
        term_months: Number(f.term_months || 360),
        annual_income: f.annual_income ? Number(f.annual_income) : null,
        vehicle_value: isAuto && f.vehicle_value ? Number(f.vehicle_value) : null,
        vehicle_purchase_price: isAuto && f.vehicle_purchase_price ? Number(f.vehicle_purchase_price) : null,
        assumption_existing_rate: isAssumption && f.assumption_existing_rate ? Number(f.assumption_existing_rate) : null,
        assumption_monthly_payment: isAssumption && f.assumption_monthly_payment ? Number(f.assumption_monthly_payment) : null,
        assumption_remaining_term: isAssumption && f.assumption_remaining_term ? Number(f.assumption_remaining_term) : null,
        relocation_incentive: isAssumption ? Number(f.relocation_incentive || 500) : null,
        additional_debts: (isAssumption || isAuto)
          ? []
          : f.additional_debts.filter((d) => d.current_lender || d.current_balance).map((d) => ({ ...d, current_balance: Number(d.current_balance || 0) })),
      });
      const inv = r.data.invite;
      const auto = r.data.automation || {};
      const esigned = !!(f.esign_authorized && f.borrower_esignature_name);
      const parts = [];
      parts.push(esigned
        ? "Client auto-approved and e-signed on the borrower's behalf (pending borrower confirmation)."
        : "Client auto-approved.");
      if (auto.borrower_notified) parts.push(`Borrower emailed${auto.borrower_sms ? " & texted" : ""} a link to confirm and pay pre-closing costs.`);
      if (auto.admin_notified) parts.push("Back-office team notified.");
      if (inv) parts.push(`Borrower login — email ${inv.email}, temp password: ${inv.temp_password}.`);
      setMsg({ ok: true, t: parts.join(" "), link: auto.pay_link || "", feeAmount: auto.fee_amount });
      onSubmitted();
    } catch (e) { setMsg({ ok: false, t: errMsg(e) }); }
    setBusy(false);
  };

  return (
    <div className="bg-[#13130E] border border-[#2A2A22] rounded-lg p-7" data-testid="broker-client-intake">
      <div className="flex items-center gap-2 mb-1"><FileCheck2 size={16} className="text-[#C9A227]" /><span className="font-serif text-lg">New Client Intake — 0% Mortgage or Auto Loan</span></div>
      <p className="text-[11px] text-[#8A8A8A] mb-5">Mortgage (refi/consolidation/assumption) or Auto/vehicle (refinance or purchase financing). You facilitate intake &amp; documents; the borrower e-signs their own closing binder.</p>

      <div className="text-[10px] uppercase tracking-[0.14em] text-[#C9A227] font-bold mb-2">Borrower Details</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div><Label>Full legal name</Label><Inp v={f.borrower_full_name} set={(v) => up("borrower_full_name", v)} testid="bc-name" /></div>
        <div><Label>Borrower email</Label><Inp v={f.borrower_email} set={(v) => up("borrower_email", v)} testid="bc-email" type="email" /></div>
        <div><Label>Phone</Label><Inp v={f.borrower_phone} set={(v) => up("borrower_phone", v)} testid="bc-phone" /></div>
        <div><Label>Date of birth</Label><Inp v={f.borrower_dob} set={(v) => up("borrower_dob", v)} testid="bc-dob" type="date" /></div>
      </div>
      <Label>Mailing address</Label>
      <Inp v={f.borrower_address} set={(v) => up("borrower_address", v)} testid="bc-address" />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div><Label>Social Security No.</Label><Inp v={f.borrower_ssn} set={(v) => up("borrower_ssn", v)} testid="bc-ssn" placeholder="XXX-XX-XXXX" /></div>
        <div><Label>Employer</Label><Inp v={f.employer} set={(v) => up("employer", v)} testid="bc-employer" /></div>
        <div><Label>Annual income</Label><Inp v={f.annual_income} set={(v) => up("annual_income", v)} testid="bc-income" type="number" /></div>
      </div>

      <div className="text-[10px] uppercase tracking-[0.14em] text-[#C9A227] font-bold mb-2 mt-4">Borrower No. 2 — Co-Borrower / Second Party <span className="text-[#6f6f66] normal-case tracking-normal font-normal">(optional — for joint owners, spouses, or additional parties to the refinance)</span></div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div><Label>Full legal name</Label><Inp v={f.co_borrower_name} set={(v) => up("co_borrower_name", v)} testid="bc-cob-name" /></div>
        <div><Label>Email</Label><Inp v={f.co_borrower_email} set={(v) => up("co_borrower_email", v)} testid="bc-cob-email" type="email" /></div>
        <div><Label>Phone</Label><Inp v={f.co_borrower_phone} set={(v) => up("co_borrower_phone", v)} testid="bc-cob-phone" /></div>
        <div><Label>Date of birth</Label><Inp v={f.co_borrower_dob} set={(v) => up("co_borrower_dob", v)} testid="bc-cob-dob" type="date" /></div>
      </div>
      <Label>Mailing address</Label>
      <Inp v={f.co_borrower_address} set={(v) => up("co_borrower_address", v)} testid="bc-cob-address" placeholder="Leave blank if same as Borrower No. 1" />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div><Label>Social Security No.</Label><Inp v={f.co_borrower_ssn} set={(v) => up("co_borrower_ssn", v)} testid="bc-cob-ssn" placeholder="XXX-XX-XXXX" /></div>
      </div>

      <div className="text-[10px] uppercase tracking-[0.14em] text-[#C9A227] font-bold mb-2 mt-4">Financing Type</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-2">
        <Sel v={f.debt_type} set={(v) => { up("debt_type", v); up("transaction_type", v === "auto" ? "refinance" : "refinance"); }} testid="bc-debt-type"
          opts={[["mortgage", "Mortgage (real property)"], ["auto", "Auto / Vehicle loan"]]} />
        <div className="rounded-md border border-[#2A2A22] bg-[#0F0F0F] px-3 py-2 text-[12px]" data-testid="bc-fee-preview">
          <span className="text-[#8A8A8A]">Pre-Closing Costs: </span>
          <span className="text-[#E4C97A] font-semibold">{isAuto ? "$850.00 / vehicle" : "$1,800.00"}</span>
          <span className="text-[#6f6f66]"> {isAuto ? "(auto — per vehicle, 50% off after the 4th)" : "(mortgage flat fee)"}</span>
        </div>
      </div>

      {isAuto && (
        <div data-testid="bc-auto-section">
          <div className="text-[10px] uppercase tracking-[0.14em] text-[#C9A227] font-bold mb-2 mt-4">Vehicle &amp; Auto Loan</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-1">
            <div><Label>Deal type</Label>
              <Sel v={f.transaction_type} set={(v) => up("transaction_type", v)} testid="bc-auto-tx-type"
                opts={[["refinance", "Refinance existing auto loan (0% note)"], ["finance", "Purchase financing (new/used vehicle)"]]} /></div>
            <div><Label>Title status</Label>
              <Sel v={f.vehicle_title_status} set={(v) => up("vehicle_title_status", v)} testid="bc-vehicle-title-status"
                opts={[["clean", "Clean title"], ["lien", "Title with existing lien"], ["salvage", "Salvage"], ["rebuilt", "Rebuilt"]]} /></div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div><Label>Year</Label><Inp v={f.vehicle_year} set={(v) => up("vehicle_year", v)} testid="bc-vehicle-year" placeholder="2021" /></div>
            <div><Label>Make</Label><Inp v={f.vehicle_make} set={(v) => up("vehicle_make", v)} testid="bc-vehicle-make" placeholder="Toyota" /></div>
            <div><Label>Model</Label><Inp v={f.vehicle_model} set={(v) => up("vehicle_model", v)} testid="bc-vehicle-model" placeholder="Camry" /></div>
            <div><Label>Trim</Label><Inp v={f.vehicle_trim} set={(v) => up("vehicle_trim", v)} testid="bc-vehicle-trim" placeholder="SE" /></div>
            <div className="sm:col-span-2"><Label>VIN</Label><Inp v={f.vehicle_vin} set={(v) => up("vehicle_vin", v)} testid="bc-vehicle-vin" placeholder="17-character VIN" /></div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div><Label>Vehicle value (NADA/KBB)</Label><Inp v={f.vehicle_value} set={(v) => up("vehicle_value", v)} testid="bc-vehicle-value" type="number" /></div>
            <div><Label>Purchase price</Label><Inp v={f.vehicle_purchase_price} set={(v) => up("vehicle_purchase_price", v)} testid="bc-vehicle-purchase-price" type="number" /></div>
            <div><Label>Title state</Label>
              <Sel v={f.vehicle_title_state} set={(v) => up("vehicle_title_state", v)} testid="bc-vehicle-title-state"
                opts={[["", "Select state…"], ...US_STATES]} /></div>
          </div>
          <div className="text-[10px] uppercase tracking-[0.14em] text-[#C9A227] font-bold mb-2 mt-3">
            {f.transaction_type === "finance" ? "Amount Financed & Lender" : "Current Lienholder & Payoff"}
          </div>
          <div className="mb-2"><Label>Find lender / bank / lienholder (auto-fill)</Label>
            <CounterpartyAutocomplete testid="bc-auto-lender-search" onSelect={(r) => { up("current_lender", r.name); if (r.address) up("current_lender_address", r.address); }} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label>{f.transaction_type === "finance" ? "Lender / dealer" : "Current lienholder"}</Label><Inp v={f.current_lender} set={(v) => up("current_lender", v)} testid="bc-auto-lender" /></div>
            <div><Label>Account / loan no.</Label><Inp v={f.account_number} set={(v) => up("account_number", v)} testid="bc-auto-account" /></div>
            <div><Label>Term (months)</Label><Inp v={f.term_months} set={(v) => up("term_months", v)} testid="bc-auto-term" type="number" /></div>
            <div><Label>{f.transaction_type === "finance" ? "Amount financed" : "Current payoff balance"}</Label><Inp v={f.current_balance} set={(v) => up("current_balance", v)} testid="bc-auto-balance" type="number" placeholder={f.transaction_type === "finance" ? "leave blank to use purchase price" : ""} /></div>
            <div><Label>Current payment</Label><Inp v={f.current_payment} set={(v) => up("current_payment", v)} testid="bc-auto-payment" type="number" /></div>
          </div>
          <div className="mt-2 rounded-md border border-[#2A2A22] bg-[#0F0F0F] p-3" data-testid="bc-auto-counterparty-bank">
            <div className="text-[10px] uppercase tracking-[0.14em] text-[#8A8A82] font-bold mb-2">Lienholder bank — for direct Fedwire payoff by a discounting bank (optional)</div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div><Label>Bank name</Label><Inp v={f.counterparty_bank_name} set={(v) => up("counterparty_bank_name", v)} testid="bc-auto-cp-bank-name" /></div>
              <div><Label>ABA / routing</Label><Inp v={f.counterparty_bank_aba} set={(v) => up("counterparty_bank_aba", v)} testid="bc-auto-cp-bank-aba" /></div>
              <div><Label>Account no.</Label><Inp v={f.counterparty_bank_account} set={(v) => up("counterparty_bank_account", v)} testid="bc-auto-cp-bank-account" /></div>
            </div>
          </div>
        </div>
      )}

      {isMortgage && (<>
      <div className="text-[10px] uppercase tracking-[0.14em] text-[#C9A227] font-bold mb-2 mt-4">Property & Existing Mortgage</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-1">
        <div><Label>Deal type</Label>
          <Sel v={f.transaction_type} set={(v) => up("transaction_type", v)} testid="bc-transaction-type"
            opts={[["refinance", "Refinance (0% note)"], ["consolidation", "Consolidation (0% note)"], ["assumption", "Mortgage Assumption (subject-to)"], ["foreclosure", "Foreclosure / Standstill (180-day)"]]} /></div>
        <div><Label>Mortgage lien position</Label>
          <Sel v={f.mortgage_position} set={(v) => up("mortgage_position", v)} testid="bc-mortgage-position"
            opts={[["first", "First mortgage (1st lien)"], ["second", "Second mortgage (2nd lien)"], ["third", "Third mortgage (3rd lien)"]]} /></div>
      </div>
      {isAssumption && (
        <div className="mb-3 rounded-md border border-[#C9A227]/40 bg-[#1a1710] p-4" data-testid="bc-assumption-fields">
          <div className="text-[10px] uppercase tracking-[0.12em] text-[#E4C97A] font-bold mb-1">Mortgage Assumption (Subject-To)</div>
          <p className="text-[11px] text-[#8A8A8A] mb-3">Seutrade takes title by deed and steps into the existing lender's note (unchanged balance/rate/payment); homeowner receives a cash-for-keys payment to vacate.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label>Existing rate (%)</Label><Inp v={f.assumption_existing_rate} set={(v) => up("assumption_existing_rate", v)} testid="bc-asm-rate" type="number" /></div>
            <div><Label>Existing monthly payment ($)</Label><Inp v={f.assumption_monthly_payment} set={(v) => up("assumption_monthly_payment", v)} testid="bc-asm-monthly" type="number" /></div>
            <div><Label>Remaining term (months)</Label><Inp v={f.assumption_remaining_term} set={(v) => up("assumption_remaining_term", v)} testid="bc-asm-term" type="number" /></div>
            <div><Label>Next payment due</Label><Inp v={f.assumption_next_due_date} set={(v) => up("assumption_next_due_date", v)} testid="bc-asm-nextdue" type="date" /></div>
            <div><Label>Cash-for-keys / move-out ($)</Label><Inp v={f.relocation_incentive} set={(v) => up("relocation_incentive", v)} testid="bc-asm-incentive" type="number" /></div>
            <div><Label>Vacate date</Label><Inp v={f.vacate_date} set={(v) => up("vacate_date", v)} testid="bc-asm-vacate" type="date" /></div>
            <div><Label>Deed to Seutrade</Label>
              <Sel v={f.deed_type} set={(v) => up("deed_type", v)} testid="bc-asm-deed-type"
                opts={[["warranty", "Warranty Deed"], ["quitclaim", "Quitclaim Deed"]]} /></div>
          </div>
        </div>
      )}
      <Label>Property address / description</Label>
      <Inp v={f.property_or_collateral} set={(v) => up("property_or_collateral", v)} testid="bc-property" placeholder="e.g. 123 Main St, Miami FL" />
      <Label>Seutrade Complex Trust name (optional — you or the borrower may name it)</Label>
      <Inp v={f.trust_name} set={(v) => up("trust_name", v)} testid="bc-trust-name" placeholder="e.g. The Alexander Family Seutrade Complex Trust" />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div><Label>Property state</Label>
          <Sel v={f.property_state} set={(v) => { up("property_state", v); up("note_structure", recommendedNoteStructure(v)); }} testid="bc-property-state"
            opts={[["", "Select state…"], ...US_STATES]} /></div>
        <div><Label>Property county</Label><Inp v={f.property_county} set={(v) => up("property_county", v)} testid="bc-property-county" placeholder="e.g. Fulton" /></div>
      </div>
      {instrumentForState(f.property_state) && (
        <div className="text-[11px] text-[#C9A227] mb-3 -mt-1" data-testid="bc-security-instrument-hint">
          Security instrument for {f.property_state}: <strong>{instrumentForState(f.property_state)}</strong> will be auto-generated for this filing.
        </div>
      )}
      <Label>Legal description (Exhibit A) — optional</Label>
      <textarea value={f.legal_description} onChange={(e) => up("legal_description", e.target.value)} data-testid="bc-legal-description" rows={3}
        placeholder="Metes & bounds or lot/block legal description (recorders often require this)"
        className="w-full bg-[#0F0F0F] border border-[#3A3A3A] px-3 py-2 rounded-sm text-[13px] mb-3" />
      <Label>Tax Parcel / APN — optional</Label>
      <Inp v={f.tax_parcel_id} set={(v) => up("tax_parcel_id", v)} testid="bc-tax-parcel" placeholder="e.g. R2001F-501" />
      <Label>Note structure</Label>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-2" data-testid="bc-note-structure">
        {[
          { k: "standard", t: "Standard (fully amortizing)", d: `${f.term_months || 360}-month term` },
          { k: "balloon_62", t: "Short-term / 62-mo balloon", d: "GA intangible-tax exempt" },
        ].map((o) => (
          <button type="button" key={o.k} onClick={() => up("note_structure", o.k)} data-testid={`bc-note-${o.k}`}
            className={`text-left px-3 py-2 rounded-sm border text-[12px] ${(f.note_structure || recommendedNoteStructure(f.property_state)) === o.k ? "border-[#C9A227] bg-[#1a1710]" : "border-[#3A3A3A]"}`}>
            <div className="font-semibold text-[#ECECE4]">{o.t}</div>
            <div className="text-[10px] text-[#8A8A8A]">{o.d}</div>
          </button>
        ))}
      </div>
      {f.property_state === "GA" && (
        <div className="text-[11px] mb-3" data-testid="bc-ga-tax-hint">
          {(f.note_structure || recommendedNoteStructure(f.property_state)) === "balloon_62"
            ? <span className="text-[#3DBB8E]">✓ Exempt from Georgia intangible recording tax (short-term note ≤ 62 months, HB 586) — saves ~${gaIntangibleTax(f.current_balance).toLocaleString()}.</span>
            : <span className="text-[#E4C97A]">Standard term would incur ~${gaIntangibleTax(f.current_balance).toLocaleString()} GA intangible recording tax. Switch to the 62-mo balloon to avoid it.</span>}
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div><Label>Property use</Label>
          <Sel v={f.property_use} set={(v) => up("property_use", v)} testid="bc-property-use"
            opts={[["residential", "Residential"], ["commercial", "Commercial"]]} /></div>
        <div><Label>Occupancy</Label>
          <Sel v={f.occupancy} set={(v) => up("occupancy", v)} testid="bc-occupancy"
            opts={[["owner_occupied", "Owner-occupied"], ["investment", "Investment"], ["second_home", "Second home"]]} /></div>
      </div>
      <div className="text-[10px] uppercase tracking-[0.14em] text-[#C9A227] font-bold mb-2 mt-1">First mortgage (1st lien)</div>
      <div className="mb-2"><Label>Find lender / bank / servicer (auto-fill)</Label>
        <CounterpartyAutocomplete testid="bc-lender-search" onSelect={(r) => { up("current_lender", r.name); if (r.address) up("current_lender_address", r.address); }} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div><Label>Current lender</Label><Inp v={f.current_lender} set={(v) => up("current_lender", v)} testid="bc-lender" /></div>
        <div><Label>Lender address</Label><Inp v={f.current_lender_address} set={(v) => up("current_lender_address", v)} testid="bc-lender-address" /></div>
        <div><Label>Account / loan no.</Label><Inp v={f.account_number} set={(v) => up("account_number", v)} testid="bc-account" /></div>
        <div><Label>Term (months)</Label><Inp v={f.term_months} set={(v) => up("term_months", v)} testid="bc-term" type="number" /></div>
        <div><Label>Current balance</Label><Inp v={f.current_balance} set={(v) => up("current_balance", v)} testid="bc-balance" type="number" /></div>
        <div><Label>Current payment</Label><Inp v={f.current_payment} set={(v) => up("current_payment", v)} testid="bc-payment" type="number" /></div>
      </div>
      <div className="mt-2 rounded-md border border-[#2A2A22] bg-[#0F0F0F] p-3" data-testid="bc-counterparty-bank">
        <div className="text-[10px] uppercase tracking-[0.14em] text-[#8A8A82] font-bold mb-2">Counterparty bank — for direct Fedwire payoff by a discounting bank (optional)</div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div><Label>Counterparty bank name</Label><Inp v={f.counterparty_bank_name} set={(v) => up("counterparty_bank_name", v)} testid="bc-cp-bank-name" /></div>
          <div><Label>ABA / routing</Label><Inp v={f.counterparty_bank_aba} set={(v) => up("counterparty_bank_aba", v)} testid="bc-cp-bank-aba" /></div>
          <div><Label>Account no.</Label><Inp v={f.counterparty_bank_account} set={(v) => up("counterparty_bank_account", v)} testid="bc-cp-bank-account" /></div>
        </div>
      </div>

      {!isAssumption && (
        <div className="mt-4 bg-[#0F0F0F] border border-[#2A2A22] rounded-md p-4" data-testid="bc-additional-mortgages">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] uppercase tracking-[0.14em] text-[#C9A227] font-bold">Additional mortgages (2nd / 3rd lien)</div>
            {f.additional_debts.length < 2 && (
              <button type="button" onClick={addDebt} data-testid="bc-add-mortgage" className="text-[10px] uppercase tracking-[0.1em] text-[#3DBB8E] hover:text-[#6fe0b6] font-bold">+ Add mortgage</button>
            )}
          </div>
          <p className="text-[11px] text-[#8A8A8A] mb-3">If the borrower has more than one mortgage, add each one here. All liens are paid off and combined into a single 0% Seutrade note.</p>
          {f.additional_debts.length === 0 && <div className="text-[11px] text-[#6C6C64] italic">No additional mortgages — this borrower has a single mortgage.</div>}
          {f.additional_debts.map((d, i) => (
            <div key={i} className="mb-3 pb-3 border-b border-[#2A2A22] last:border-0" data-testid={`bc-mortgage-row-${i}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-[11px] text-[#C9C9C0] font-semibold">{i === 0 ? "Second (2nd)" : "Third (3rd)"} mortgage</span>
                <button type="button" onClick={() => rmDebt(i)} data-testid={`bc-rm-mortgage-${i}`} className="text-[10px] text-[#FF8585] uppercase tracking-[0.1em]">Remove</button>
              </div>
              <div className="mb-2"><Label>Find lender / bank / servicer (auto-fill)</Label>
                <CounterpartyAutocomplete testid={`bc-mortgage-search-${i}`} onSelect={(r) => { upDebt(i, "current_lender", r.name); if (r.address) upDebt(i, "current_lender_address", r.address); }} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label>Lender / servicer</Label><Inp v={d.current_lender} set={(v) => upDebt(i, "current_lender", v)} testid={`bc-mortgage-lender-${i}`} /></div>
                <div><Label>Account / loan no.</Label><Inp v={d.account_number} set={(v) => upDebt(i, "account_number", v)} testid={`bc-mortgage-account-${i}`} /></div>
                <div><Label>Lender address</Label><Inp v={d.current_lender_address} set={(v) => upDebt(i, "current_lender_address", v)} testid={`bc-mortgage-address-${i}`} /></div>
                <div><Label>Balance</Label><Inp v={d.current_balance} set={(v) => upDebt(i, "current_balance", v)} testid={`bc-mortgage-balance-${i}`} type="number" /></div>
              </div>
            </div>
          ))}
          {f.additional_debts.length > 0 && (
            <div className="text-[11px] text-[#3DBB8E] mt-1 font-semibold" data-testid="bc-consolidation-total">
              Total to consolidate into one Seutrade note: ${((Number(f.current_balance || 0)) + f.additional_debts.reduce((s, d) => s + Number(d.current_balance || 0), 0)).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
          )}
        </div>
      )}
      </>)}

      <div className="text-[10px] uppercase tracking-[0.14em] text-[#C9A227] font-bold mb-2 mt-4">Broker Intake Checklist</div>
      <div className="space-y-2 bg-[#0F0F0F] border border-[#2A2A22] rounded-md p-4">
        {CHECKLIST_ITEMS.map((c) => (
          <label key={c.k} className="flex items-center gap-2.5 cursor-pointer" data-testid={`bc-check-${c.k}`}>
            <input type="checkbox" checked={!!checklist[c.k]} onChange={(e) => setChecklist((s) => ({ ...s, [c.k]: e.target.checked }))} className="accent-[#C9A227] w-4 h-4" />
            <span className="text-[12px] text-[#C9C9C0]">{c.t}</span>
          </label>
        ))}
      </div>

      <div className="text-[10px] uppercase tracking-[0.14em] text-[#C9A227] font-bold mb-2 mt-4">Borrower E-Signature (optional — sign on their behalf)</div>
      <div className="bg-[#0F0F0F] border border-[#2A2A22] rounded-md p-4 space-y-3" data-testid="bc-esign-block">
        <label className="flex items-start gap-2.5 cursor-pointer" data-testid="bc-esign-authorize">
          <input type="checkbox" checked={!!f.esign_authorized} onChange={(e) => up("esign_authorized", e.target.checked)} className="accent-[#C9A227] w-4 h-4 mt-0.5" />
          <span className="text-[12px] text-[#C9C9C0]">I am authorized by the borrower to e-sign their financing package now. Their typed name will be applied as their e-signature on every document. They will receive a link to <strong>confirm</strong> it before it is final.</span>
        </label>
        {f.esign_authorized && (
          <div className="grid grid-cols-1 gap-3 pt-1">
            <div>
              <Label>Borrower's typed e-signature (full legal name)</Label>
              <Inp v={f.borrower_esignature_name} set={(v) => up("borrower_esignature_name", v)} testid="bc-esign-name" />
              {f.borrower_esignature_name && (
                <div className="mt-2 px-3 py-2 bg-[#13130E] border border-[#2A2A22] rounded-sm">
                  <span className="text-[10px] uppercase tracking-[0.12em] text-[#8A8A8A]">Signature preview</span>
                  <div className="text-[22px] text-[#E4C97A] mt-0.5" style={{ fontFamily: "'Brush Script MT', cursive" }} data-testid="bc-esign-preview">{f.borrower_esignature_name}</div>
                </div>
              )}
            </div>
            <div>
              <Label>Attestation (optional)</Label>
              <Inp v={f.esign_attestation} set={(v) => up("esign_attestation", v)} testid="bc-esign-attestation" placeholder="I have reviewed and agree to the terms of my financing package." />
            </div>
            {f.co_borrower_name && (
              <div>
                <Label>Co-borrower's typed e-signature ({f.co_borrower_name})</Label>
                <Inp v={f.co_borrower_esignature_name} set={(v) => up("co_borrower_esignature_name", v)} testid="bc-co-esign-name" />
                {f.co_borrower_esignature_name && (
                  <div className="mt-2 px-3 py-2 bg-[#13130E] border border-[#2A2A22] rounded-sm">
                    <span className="text-[10px] uppercase tracking-[0.12em] text-[#8A8A8A]">Co-borrower signature preview</span>
                    <div className="text-[22px] text-[#E4C97A] mt-0.5" style={{ fontFamily: "'Brush Script MT', cursive" }} data-testid="bc-co-esign-preview">{f.co_borrower_esignature_name}</div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <button onClick={submit} disabled={busy || !allChecked || !f.borrower_full_name || !f.borrower_email || (isMortgage ? (!f.current_lender || !f.current_balance) : (!f.current_balance && !f.vehicle_purchase_price)) || (f.esign_authorized && !f.borrower_esignature_name.trim())} data-testid="bc-submit"
        className="w-full mt-4 bg-[#C9A227] hover:bg-[#b8901f] disabled:opacity-40 disabled:cursor-not-allowed text-[#0A0A0A] px-4 py-2.5 text-[11px] uppercase tracking-[0.16em] font-bold rounded-sm flex items-center justify-center gap-2">
        {busy ? <Loader2 size={14} className="animate-spin" /> : null}{f.esign_authorized ? "Submit, E-Sign & Notify Borrower" : "Submit Client (Auto-Approve)"}
      </button>
      {!allChecked && <div className="text-[11px] text-[#8A8A8A] mt-2 text-center">Complete the intake checklist to enable submission.</div>}
      {msg && <div className={`text-[12px] mt-3 leading-relaxed ${msg.ok ? "text-[#7DD3B8]" : "text-[#FF8585]"}`} data-testid="bc-result">{msg.ok ? "✓" : "✗"} {msg.t}</div>}
      {msg && msg.ok && msg.link && (
        <div className="mt-3 bg-[#0F0F0F] border border-[#B8860B]/40 rounded-md p-3" data-testid="bc-pay-link-box">
          <div className="text-[10px] uppercase tracking-[0.14em] text-[#C9A227] font-bold mb-1.5">Borrower pay &amp; confirm link{msg.feeAmount ? ` · pre-closing ${Number(msg.feeAmount).toLocaleString("en-US",{style:"currency",currency:"USD",maximumFractionDigits:0})}` : ""}</div>
          <div className="flex items-center gap-2">
            <input readOnly value={msg.link} data-testid="bc-pay-link-input" onFocus={(e)=>e.target.select()}
              className="flex-1 bg-[#13130E] border border-[#2A2A22] rounded-sm px-2.5 py-1.5 text-[11px] text-[#C9C9C0] font-mono" />
            <button type="button" data-testid="bc-pay-link-copy"
              onClick={() => { navigator.clipboard?.writeText(msg.link); setLinkCopied(true); setTimeout(()=>setLinkCopied(false),1800); }}
              className="inline-flex items-center gap-1 bg-[#C9A227] hover:bg-[#b8901f] text-[#0A0A0A] px-3 py-1.5 text-[10px] uppercase tracking-[0.1em] font-bold rounded-sm whitespace-nowrap">
              {linkCopied ? <><Check size={12}/> Copied</> : <><Copy size={12}/> Copy link</>}
            </button>
          </div>
          <div className="text-[10.5px] text-[#8A8A8A] mt-1.5">Send this to the borrower — they sign in, confirm their e-signature, and pay their pre-closing costs.</div>
        </div>
      )}
    </div>
  );
};

const PHASES = [
  { key: "intake", label: "Intake", icon: ClipboardCheck },
  { key: "approval", label: "Approval", icon: CheckCircle2 },
  { key: "disbursement", label: "Disbursement", icon: Truck },
  { key: "closing", label: "Closing", icon: PenLine },
];
const PHASE_TEXT = {
  intake: { success: "Intake complete", pending: "Pending" },
  approval: { pending: "Awaiting admin", success: "Approved", rejected: "Rejected" },
  disbursement: { pending: "Not started", submitted: "Submitted to prior lender", success: "Disbursed" },
  closing: { pending: "Not started", in_progress: "Awaiting borrower e-sign", success: "Closed & e-signed" },
};
const phaseColor = (st) => st === "success" ? "text-[#3DBB8E] border-[#0B6B4F] bg-[#0B6B4F]/15"
  : st === "rejected" ? "text-[#FF8585] border-[#9E2A2B]/60 bg-[#9E2A2B]/15"
  : st === "pending" ? "text-[#6C6C64] border-[#2A2A22] bg-[#0F0F0F]"
  : "text-[#E4C97A] border-[#B8860B]/50 bg-[#B8860B]/10"; // submitted / in_progress

const ClientPipeline = ({ clients, authAxios, token, onChange }) => {
  if (!clients.length) return (
    <div data-testid="broker-clients">
      <h3 className="font-serif text-xl mb-3">Client Pipeline</h3>
      <div className="text-[13px] text-[#5C5C5C] bg-[#13130E] border border-[#2A2A22] rounded-lg p-6">No clients yet. Submit your first client above.</div>
    </div>
  );
  return (
    <div data-testid="broker-clients">
      <h3 className="font-serif text-xl mb-4">Client Pipeline</h3>
      <div className="space-y-4">
        {clients.map((c) => <ClientCard key={c.id} c={c} authAxios={authAxios} token={token} onChange={onChange} />)}
      </div>
    </div>
  );
};

const SigPayChips = ({ c }) => {
  const sigs = c.signatures || [];
  const signed = sigs.length > 0;
  const pending = !!c.esign_pending_confirmation;
  const chip = (label, tone, testid) => (
    <span data-testid={testid} className={`px-2 py-0.5 text-[9.5px] uppercase tracking-[0.1em] border rounded-sm ${tone}`}>{label}</span>
  );
  const green = "text-[#3DBB8E] border-[#0B6B4F]/60 bg-[#0B6B4F]/12";
  const amber = "text-[#E4C97A] border-[#B8860B]/50 bg-[#B8860B]/10";
  const muted = "text-[#8A8A8A] border-[#2A2A22]";
  return (
    <>
      {signed
        ? (pending ? chip("Signed · unconfirmed", amber, `broker-sig-chip-${c.id}`) : chip("Signed · confirmed", green, `broker-sig-chip-${c.id}`))
        : chip("Unsigned", muted, `broker-sig-chip-${c.id}`)}
      {c.status === "approved" && (
        c.fee_waived ? chip("No cost", green, `broker-pay-chip-${c.id}`)
          : c.closing_fees_paid ? chip("Fee paid", green, `broker-pay-chip-${c.id}`)
          : chip("Fee due", amber, `broker-pay-chip-${c.id}`)
      )}
    </>
  );
};

const ClientCard = ({ c, authAxios, token, onChange }) => {
  const fileRef = useRef(null);
  const [docType, setDocType] = useState("government_id");
  const [uploading, setUploading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [showAccount, setShowAccount] = useState(false);
  const pl = c.pipeline || {};
  const comm = c.commission || {};
  const secInstr = (c.binder && c.binder.securityInstrument) || null;
  const [worksheet, setWorksheet] = useState(null);
  const [refinancing, setRefinancing] = useState(false);
  const bns = (c.binder && c.binder.noteStructure) || {};
  const btila = (c.binder && c.binder.tila) || {};
  const isBalloon = Number(bns.balloonPayment || 0) > 0 && Number(bns.maturityMonths || 0) < Number(bns.amortizationMonths || 0);
  const balloonFinal = Number(btila.final_payment != null ? btila.final_payment : bns.balloonPayment || 0);
  const balloonMaturity = btila.maturity_date;
  const canRefiBalloon = c.status === "approved" && isBalloon && (bns.refinanceOption || {}).enabled && !c.refinanced_to;

  const refinanceBalloon = async () => {
    if (!window.confirm(`Refinance ${c.borrower_name}'s balloon at maturity into a new 62-month short-term note? The outstanding balance rolls forward (no lump-sum payoff) and the successor note remains GA intangible-tax exempt.`)) return;
    setRefinancing(true);
    try {
      await authAxios.post(`/broker/clients/${c.id}/refinance-balloon`);
      onChange();
      alert("Balloon refinanced. A new short-term note has been added to this client's pipeline.");
    } catch (err) { alert(errMsg(err)); }
    setRefinancing(false);
  };

  const downloadSecurityInstrument = async () => {
    try {
      const r = await authAxios.get(`/broker/clients/${c.id}/security-instrument.pdf`, { responseType: "blob" });
      const u = URL.createObjectURL(r.data);
      window.open(u, "_blank");
    } catch (err) { alert(errMsg(err)); }
  };
  const openWorksheet = async () => {
    try { const r = await authAxios.get(`/broker/clients/${c.id}/deed-worksheet`); setWorksheet(r.data); }
    catch (err) { alert(errMsg(err)); }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("doc_type", docType);
      await authAxios.post(`/broker/clients/${c.id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange();
    } catch (err) { alert(errMsg(err)); }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <div className="bg-[#13130E] border border-[#2A2A22] rounded-lg p-5" data-testid={`broker-client-${c.id}`}>
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="font-semibold text-[15px]">{c.borrower_name} <span className="text-[#8A8A8A] text-[12px]">· {c.debt_type === "auto" ? (c.transaction_type === "finance" ? "Auto Purchase" : "Auto Refinance") : `${c.property_use === "commercial" ? "Commercial" : "Residential"} ${c.transaction_type === "assumption" ? "Mortgage Assumption" : c.transaction_type === "consolidation" ? "0% Consolidation" : "0% Refi"}`}</span>
            {c.routed_by_borrower ? <span className="ml-2 inline-block px-2 py-0.5 rounded-full text-[9px] uppercase tracking-[0.12em] font-bold bg-[#3DBB8E]/15 text-[#5fd6ab] border border-[#0B6B4F]/50" data-testid={`broker-routed-tag-${c.id}`}>Borrower-routed</span> : null}
            {c.is_linked_client ? <span className="ml-2 inline-block px-2 py-0.5 rounded-full text-[9px] uppercase tracking-[0.12em] font-bold bg-[#6C8CD5]/15 text-[#9db6ea] border border-[#6C8CD5]/50" data-testid={`broker-linked-tag-${c.id}`} title="Shared with you via an access link">Linked · {c.brokerage_label}</span> : null}</div>
          <div className="text-[12px] text-[#9A9A90] mt-1">{c.current_lender} · {c.property_or_collateral || "—"}</div>
          {c.transaction_type === "assumption" ? (
            <div className="text-[11.5px] text-[#8A8A8A] mt-1">Assumes existing {fmt(c.current_balance)} note · {fmt(c.refi?.new_monthly_payment)}/mo @ {Number(c.refi?.new_rate || 0).toFixed(2)}% over {c.refi?.term_months} mo · cash-for-keys {fmt(c.relocation_incentive || 500)}</div>
          ) : (
            <div className="text-[11.5px] text-[#8A8A8A] mt-1">Balance {fmt(c.current_balance)} → 0% note {fmt(c.refi?.new_principal)} · {fmt(c.refi?.new_monthly_payment)}/mo over {c.refi?.term_months} mo</div>
          )}
          {c.transaction_type === "assumption" && (
            <div className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-[#C8A24B]/60 bg-[#C8A24B]/12 px-2.5 py-1 text-[10.5px] font-semibold text-[#E4C97A]" data-testid={`broker-assumption-chip-${c.id}`}>
              <Repeat size={11} /> Assumption · subject-to{c.mortgage_position && c.mortgage_position !== "first" ? ` · ${c.mortgage_position} lien` : ""}
            </div>
          )}
          {isBalloon && (
            <div className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-[#C8A24B]/50 bg-[#C8A24B]/10 px-2.5 py-1 text-[10.5px] font-semibold text-[#E4C97A]" data-testid={`broker-balloon-chip-${c.id}`}>
              Balloon {fmt(balloonFinal)} due {balloonMaturity || "at maturity"} (mo {bns.maturityMonths})
              {bns.gaShortTermExempt && <span className="text-[#3DBB8E]"> · GA tax-exempt</span>}
            </div>
          )}
          {c.refinanced_to && <div className="mt-1 text-[10.5px] text-[#3DBB8E]" data-testid={`broker-balloon-refinanced-${c.id}`}>✓ Balloon refinanced into a successor short-term note</div>}
          {c.last_edited_by && <div className="text-[10.5px] text-[#6E6E64] mt-1" data-testid={`broker-last-edited-${c.id}`}>Last edited by {c.last_edited_by}{c.last_edited_role ? ` (${c.last_edited_role})` : ""}{c.last_edited_at ? ` · ${new Date(c.last_edited_at).toLocaleDateString()}` : ""}{c.resign_required ? " · re-signature required" : ""}</div>}
          {secInstr && (
            <button onClick={downloadSecurityInstrument} data-testid={`broker-security-instrument-${c.id}`}
              className="inline-flex items-center gap-1.5 mt-2 text-[11px] text-[#9DB4E8] hover:text-[#c3d3f5]">
              <FileCheck2 size={12} /> {secInstr.label}{secInstr.state_name ? ` · ${secInstr.state_name}` : ""} — download
            </button>
          )}
          {secInstr && (
            <button onClick={openWorksheet} data-testid={`broker-deed-worksheet-${c.id}`}
              className="inline-flex items-center gap-1.5 mt-2 ml-4 text-[11px] text-[#C9A227] hover:text-[#e4c97a]">
              <ClipboardList size={12} /> Recording worksheet
            </button>
          )}
          {worksheet && <DeedWorksheetModal worksheet={worksheet} onClose={() => setWorksheet(null)} />}
          <button onClick={() => window.open(`/tax-credit/${c.id}`, "_blank")} data-testid={`broker-tax-credit-${c.id}`}
            className="inline-flex items-center gap-1.5 mt-2 text-[11px] text-[#6FD3AE] hover:text-[#9bead0]">
            <FileCheck2 size={12} /> Tax Credit · Settle IRS debt
          </button>
          {canRefiBalloon && (
            <div className="mt-2">
              <button onClick={refinanceBalloon} disabled={refinancing} data-testid={`broker-refi-balloon-${c.id}`}
                className="inline-flex items-center gap-1.5 rounded-sm border border-[#C8A24B]/60 bg-[#C8A24B]/10 px-3 py-1.5 text-[10.5px] uppercase tracking-[0.12em] font-bold text-[#E4C97A] hover:bg-[#C8A24B]/20 disabled:opacity-50">
                {refinancing ? "Refinancing…" : "Refinance Balloon at Maturity"}
              </button>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {c.status !== "rejected" && (
            <button onClick={() => setEditing((e) => !e)} data-testid={`broker-edit-toggle-${c.id}`}
              className="inline-flex items-center gap-1.5 border border-[#C9A227]/50 hover:border-[#C9A227] text-[#E4C97A] px-3 py-1.5 text-[10px] uppercase tracking-[0.12em] font-bold rounded-sm">
              <Pencil size={12} /> {editing ? "Close" : "Edit"}
            </button>
          )}
          {!(c.signatures && c.signatures.length) && c.status !== "rejected" && (
            <button data-testid={`broker-esign-${c.id}`} onClick={async () => {
              const name = window.prompt("E-SIGN BORROWER'S PACKAGE\n\nType the borrower's full legal name to apply their e-signature to every document. This signature is FINAL.", c.borrower_name || "");
              if (!name || !name.trim()) return;
              const co = window.prompt("Co-borrower's full legal name (optional):", "") || "";
              try { await authAxios.post(`/broker/clients/${c.id}/staff-esign`, { signer_name: name.trim(), co_signer_name: co.trim() || null }); onChange(); }
              catch (e) { alert(e.response?.data?.detail || e.message); }
            }} className="inline-flex items-center gap-1.5 border border-[#5C6B9E]/50 hover:border-[#5C6B9E] text-[#9DB4E8] px-3 py-1.5 text-[10px] uppercase tracking-[0.12em] font-bold rounded-sm">
              <Pencil size={12} /> E-Sign Package
            </button>
          )}
          <button data-testid={`broker-delete-${c.id}`} onClick={async () => {
            if (!window.confirm(`Delete ${c.borrower_name}'s file? It will be archived (hidden from your list) and can be restored by an admin. Documents and payments are preserved.`)) return;
            try { await authAxios.post(`/broker/clients/${c.id}/archive`, {}); onChange(); }
            catch (e) { alert(e.response?.data?.detail || e.message); }
          }} className="inline-flex items-center gap-1.5 border border-[#7A3A3A]/50 hover:border-[#9E2A2B] text-[#FF8585] px-3 py-1.5 text-[10px] uppercase tracking-[0.12em] font-bold rounded-sm">
            <Trash2 size={12} /> Delete
          </button>
          <span className={`px-2.5 py-1 text-[10px] uppercase tracking-[0.12em] border rounded-sm ${c.status === "approved" ? "text-[#3DBB8E] border-[#0B6B4F]/60 bg-[#0B6B4F]/15" : c.status === "rejected" ? "text-[#FF8585] border-[#9E2A2B]/60 bg-[#9E2A2B]/15" : "text-[#E4C97A] border-[#B8860B]/50 bg-[#B8860B]/10"}`} data-testid={`broker-client-status-${c.id}`}>{c.status}</span>
          <SigPayChips c={c} />
        </div>
      </div>

      {editing && c.status !== "rejected" && (
        <EditClientForm c={c} authAxios={authAxios} onSaved={() => { setEditing(false); onChange(); }} onCancel={() => setEditing(false)} />
      )}

      <SubmitToLender app={c} authAxios={authAxios} base="broker/clients" onChanged={onChange} />

      {/* Pipeline monitor */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-4" data-testid={`broker-pipeline-${c.id}`}>
        {PHASES.map((ph, i) => {
          const st = pl[ph.key] || "pending";
          const label = (PHASE_TEXT[ph.key] || {})[st] || st;
          return (
            <div key={ph.key} className={`border rounded-md p-3 ${phaseColor(st)}`} data-testid={`phase-${ph.key}-${c.id}`}>
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.1em] font-bold">
                <span className="opacity-70">{i + 1}.</span> <ph.icon size={12} /> {ph.label}
              </div>
              <div className="text-[11px] mt-1 font-semibold">{st === "success" ? "✓ " : ""}{label}</div>
            </div>
          );
        })}
      </div>
      {pl.rejected_reason && <div className="text-[11px] text-[#FF8585] mt-2">Reason: {pl.rejected_reason}</div>}

      {/* Borrower e-sign of the closing binder */}
      {c.status === "approved" && (
        <div className="mt-4 bg-[#0F0F0F] border border-[#B8860B]/30 rounded-md p-3" data-testid={`broker-esign-${c.id}`}>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <div className="text-[10px] uppercase tracking-[0.12em] text-[#C9A227] font-bold">Closing Binder — Borrower E-Signature</div>
              <div className="text-[11.5px] mt-1 text-[#C9C9C0]">
                Status: <span className={pl.closing === "success" ? "text-[#3DBB8E]" : "text-[#E4C97A]"}>
                  {pl.closing === "success" ? "✓ Closed & e-signed by borrower" : "Awaiting borrower e-signature"}
                </span>
              </div>
              {c.resign_required && (
                <div className="text-[11px] mt-1.5 text-[#F0DFA8] bg-[#7A5B00]/25 border border-[#B8860B]/50 rounded px-2.5 py-1.5" data-testid={`broker-resign-${c.id}`}>
                  ⚠ Documents were updated — the borrower must <strong>re-sign</strong> the corrected binder.
                </div>
              )}
              <div className="text-[10.5px] text-[#8A8A8A] mt-1">The borrower e-signs their own binder in the Borrower Portal. SEUTRADE FINANCIAL SERVICES auto-endorses once they sign.</div>
            </div>
            <div className="flex flex-col gap-1.5">
              <a href={`/broker/binder/${c.id}`} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-[0.12em] font-bold bg-[#B8860B]/15 hover:bg-[#B8860B]/30 text-[#E4C97A] border border-[#B8860B]/40 rounded-sm" data-testid={`broker-preview-binder-${c.id}`}>
                <FileText size={12} /> Preview Binder
              </a>
              <button onClick={() => { navigator.clipboard?.writeText(`${window.location.origin}/borrower`); alert("Borrower e-sign link copied. Share it with the borrower — they log in with their credentials to e-sign."); }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-[0.12em] font-bold bg-[#2A2A22] hover:bg-[#3A3A2E] text-[#B8C4B8] border border-[#3A3A2E] rounded-sm" data-testid={`broker-esign-link-${c.id}`}>
                Copy Borrower E-Sign Link
              </button>
            </div>
          </div>
          {Array.isArray(c.binder?.mortgages) && c.binder.mortgages.length > 1 && (
            <div className="mt-3 pt-3 border-t border-[#2A2A22]" data-testid={`broker-perlien-${c.id}`}>
              <div className="text-[9px] uppercase tracking-[0.14em] text-[#8A8A82] font-bold mb-1.5">Per-lien files — each lender gets its own closing + funding binder</div>
              <div className="flex flex-wrap gap-1.5">
                {c.binder.mortgages.map((m, i) => (
                  <span key={i} className="inline-flex items-center gap-1 rounded-sm border border-[#3A3A2E] bg-[#161610] px-1.5 py-1">
                    <span className="text-[9px] uppercase tracking-[0.08em] text-[#C9A227] font-bold pr-0.5" title={m.lender}>{m.position?.[0]?.toUpperCase() || (i + 1)}· {(m.lender || "Lender").slice(0, 16)}</span>
                    <a href={`/closing-binder/${c.id}?lien=${i}`} target="_blank" rel="noreferrer"
                      className="inline-flex items-center gap-1 px-2 py-0.5 text-[9px] uppercase tracking-[0.06em] bg-[#2A2A22] hover:bg-[#3A3A2E] text-[#B8C4B8] border border-[#3A3A2E] rounded-sm"
                      data-testid={`broker-closing-lien-${c.id}-${i}`}>
                      <FileText size={10} /> Closing
                    </a>
                    <a href={`/draft-package/${c.id}?lien=${i}`} target="_blank" rel="noreferrer"
                      className="inline-flex items-center gap-1 px-2 py-0.5 text-[9px] uppercase tracking-[0.06em] bg-[#0e2a6b]/20 hover:bg-[#0e2a6b]/40 text-[#9DB4E8] border border-[#0e2a6b]/50 rounded-sm"
                      data-testid={`broker-draft-lien-${c.id}-${i}`}>
                      <FileText size={10} /> Draft
                    </a>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {c.status === "approved" && <BrokerPaymentsPanel c={c} authAxios={authAxios} />}

      <BorrowerAccessPanel c={c} authAxios={authAxios} />

      {/* Commission + documents */}
      <div className="mt-4 grid md:grid-cols-2 gap-4">
        <div className="bg-[#0F0F0F] border border-[#2A2A22] rounded-md p-3">
          {comm.referral_only ? (
            <>
              <div className="text-[10px] uppercase tracking-[0.12em] text-[#C9A227] font-bold mb-1">Your referral fee</div>
              <div className="text-[13px] text-[#E4C97A] font-semibold" data-testid={`broker-referral-fee-${c.id}`}>{fmt(comm.referral_fee || comm.admin_fee || 150)} <span className="text-[10px] text-[#8A8A8A] font-normal">· flat, one-time (no perpetual commission)</span></div>
              <div className="text-[11px] mt-1" data-testid={`broker-admin-fee-${c.id}`}>
                {comm.admin_fee_paid
                  ? <span className="text-[#3DBB8E] font-semibold">paid to you{comm.admin_fee_paid_at ? ` · ${new Date(comm.admin_fee_paid_at).toLocaleDateString()}` : ""}</span>
                  : comm.admin_fee_eligible
                    ? <span className="text-[#E4C97A]">queued — transfers automatically</span>
                    : <span className="text-[#8A8A8A]">paid to you once the borrower's closing costs clear</span>}
              </div>
            </>
          ) : (
            <>
              <div className="text-[10px] uppercase tracking-[0.12em] text-[#C9A227] font-bold mb-1">Your commission</div>
              <div className="text-[13px] text-[#E4C97A] font-semibold" data-testid={`broker-comm-monthly-${c.id}`}>{fmt(comm.monthly_commission)}/mo <span className="text-[10px] text-[#8A8A8A] font-normal">· {fmt(comm.annual_commission)}/yr (current {Math.round((comm.current_rate || 0.02) * 100)}% tier)</span></div>
              <div className="text-[12px] text-[#C9C9C0] mt-1">Tier schedule: Yr1 {fmt((comm.monthly_payment || 0) * 0.02)}/mo · Yr2 {fmt((comm.monthly_payment || 0) * 0.03)}/mo · Yr3 {fmt((comm.monthly_payment || 0) * 0.04)}/mo · Yr4+ {fmt((comm.monthly_payment || 0) * 0.05)}/mo</div>
              <div className="text-[11px] text-[#8A8A8A] mt-1">2% accrues on each monthly payment your client makes · Accrued to date: <span className="text-[#C9A227] font-semibold">{fmt(comm.accrued)}</span> ({comm.paid_installments || 0} payment{(comm.paid_installments || 0) !== 1 ? "s" : ""} received)</div>
              <div className="text-[11px] mt-1" data-testid={`broker-admin-fee-${c.id}`}>
                + one-time <span className="text-[#C9A227] font-semibold">{fmt(comm.admin_fee || 250)}</span> Administrative Fee →{" "}
                {comm.admin_fee_paid
                  ? <span className="text-[#3DBB8E] font-semibold">paid to you{comm.admin_fee_paid_at ? ` · ${new Date(comm.admin_fee_paid_at).toLocaleDateString()}` : ""}</span>
                  : comm.admin_fee_eligible
                    ? <span className="text-[#E4C97A]">queued — transfers automatically</span>
                    : <span className="text-[#8A8A8A]">paid to you once the borrower's closing costs clear</span>}
              </div>
            </>
          )}
        </div>
        <div className="bg-[#0F0F0F] border border-[#2A2A22] rounded-md p-3">
          <div className="text-[10px] uppercase tracking-[0.12em] text-[#C9A227] font-bold mb-2">Documents ({(c.documents || []).length})</div>
          <div className="space-y-1 mb-2 max-h-24 overflow-auto">
            {(c.documents || []).map((d) => (
              <a key={d.id} href={`${API}/broker/documents/${d.id}?auth=${token}`} target="_blank" rel="noreferrer"
                className="flex items-center gap-1.5 text-[11px] text-[#7DD3B8] hover:text-[#3DBB8E]" data-testid={`broker-doc-${d.id}`}>
                <FileText size={12} /> {d.original_filename} <span className="text-[#5C5C5C]">· {d.doc_type}</span>
                {d.review_status && d.review_status !== "pending" && (
                  <span className={`px-1 py-0.5 text-[8px] uppercase tracking-[0.08em] border rounded-sm ${d.review_status === "verified" ? "text-[#3DBB8E] border-[#0B6B4F]/50" : "text-[#FF8585] border-[#9E2A2B]/50"}`}>
                    {d.review_status === "verified" ? "verified" : "attention"}
                  </span>
                )}
              </a>
            ))}
            {(c.documents || []).length === 0 && <div className="text-[11px] text-[#5C5C5C]">No documents uploaded.</div>}
          </div>
          <div className="flex items-center gap-2">
            <Sel v={docType} set={setDocType} testid={`broker-doctype-${c.id}`} small
              opts={[["government_id", "Gov ID"], ["income", "Income"], ["mortgage_statement", "Mortgage stmt"], ["property_deed", "Deed/Title"], ["other", "Other"]]} />
            <input ref={fileRef} type="file" onChange={onFile} className="hidden" data-testid={`broker-file-input-${c.id}`}
              accept=".pdf,.png,.jpg,.jpeg,.webp,.heic,.doc,.docx" />
            <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid={`broker-upload-${c.id}`}
              className="inline-flex items-center gap-1.5 bg-[#C9A227] hover:bg-[#b8901f] disabled:opacity-50 text-[#0A0A0A] px-3 py-1.5 text-[10px] uppercase tracking-[0.1em] font-bold rounded-sm">
              {uploading ? <Loader2 size={12} className="animate-spin" /> : <UploadCloud size={12} />} Upload
            </button>
          </div>
        </div>
      </div>

      {/* Account details & internal notes */}
      <div className="mt-4">
        <button onClick={() => setShowAccount((v) => !v)} className="text-[11px] uppercase tracking-[0.1em] font-bold text-[#E4C97A] inline-flex items-center gap-1.5 hover:text-[#F0DFA8]" data-testid={`broker-account-toggle-${c.id}`}>
          {showAccount ? "▾ Hide account details & notes" : "▸ Account details & notes"}
        </button>
        {showAccount && (
          <div className="mt-3">
            <AccountDetailsPanel application={c} editable showNotes theme="dark" actorRole="broker"
              onSaveProfile={async (profile) => { await authAxios.put(`/broker/clients/${c.id}/account-profile`, { profile }); onChange(); }}
              onAddNote={async (text) => { const r = await authAxios.post(`/broker/clients/${c.id}/account-notes`, { text }); onChange(); return r.data.application?.account_notes || []; }}
              onEditNote={async (noteId, text) => { const r = await authAxios.put(`/broker/clients/${c.id}/account-notes/${noteId}`, { text }); onChange(); return r.data.application?.account_notes || []; }}
              onDeleteNote={async (noteId) => { const r = await authAxios.delete(`/broker/clients/${c.id}/account-notes/${noteId}`); onChange(); return r.data.application?.account_notes || []; }}
              onAttach={async (noteId, file) => { const fd = new FormData(); fd.append("file", file); const r = await authAxios.post(`/broker/clients/${c.id}/account-notes/${noteId}/attachment`, fd); onChange(); return r.data.application?.account_notes || []; }}
              attachmentUrl={(noteId, attId) => `${API}/broker/clients/${c.id}/account-notes/${noteId}/attachment/${attId}?auth=${encodeURIComponent(localStorage.getItem("th_broker_token") || "")}`} />
          </div>
        )}
      </div>
    </div>
  );
};

// ---- shared inputs ----
const BrokerPaymentsPanel = ({ c, authAxios }) => {
  const [busy, setBusy] = useState(null);
  const ap = c.autopay || {};
  const onFile = ap.enrolled;
  const sched = c.schedule || [];
  const paidCount = sched.filter((s) => s.status === "paid").length;
  const nextDue = sched.find((s) => s.status !== "paid");
  const cf = c.closing_fees || {};
  const go = async (path, label) => {
    setBusy(label);
    try {
      const r = await authAxios.post(`/broker/clients/${c.id}/${path}`, { origin_url: window.location.origin });
      if (r.data.url) window.location.href = r.data.url;
      else { toast.error("Could not start the payment."); setBusy(null); }
    } catch (e) { toast.error(errMsg(e)); setBusy(null); }
  };
  const Btn = ({ label, path, icon: Icon, testid, primary }) => (
    <button onClick={() => go(path, label)} disabled={!!busy} data-testid={testid}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-[0.12em] font-bold rounded-sm border disabled:opacity-50 ${primary ? "bg-[#C9A227] hover:bg-[#b8901f] text-[#0A0A0A] border-[#C9A227]" : "bg-[#2A2A22] hover:bg-[#3A3A2E] text-[#E4C97A] border-[#3A3A2E]"}`}>
      {busy === label ? <Loader2 size={12} className="animate-spin" /> : <Icon size={12} />} {label}
    </button>
  );
  return (
    <div className="mt-4 bg-[#0F0F0F] border border-[#C9A227]/30 rounded-md p-3" data-testid={`broker-payments-${c.id}`}>
      <div className="text-[10px] uppercase tracking-[0.12em] text-[#C9A227] font-bold mb-1">Borrower Payments (on the borrower's behalf)</div>
      <div className="text-[10.5px] text-[#8A8A8A] mb-2">
        {onFile
          ? <>Payment method on file: <span className="text-[#B8C4B8]">{(ap.card_brand || ap.method || "card")}{ap.card_last4 ? ` ····${ap.card_last4}` : ""}</span>{ap.set_up_by === "broker" ? " · set up by you" : ""}.</>
          : "No payment method on file yet. Set up the borrower's debit card or bank account securely."}
        {" "}Card/bank details are entered on Stripe's secure page (never stored on our servers).
      </div>
      {c.status === "approved" && (
        <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-[10.5px] mb-2.5 max-w-md" data-testid={`broker-pay-summary-${c.id}`}>
          <span className="text-[#8A8A8A]">Closing costs</span>
          <span className={`text-right font-mono ${c.closing_fees_paid ? "text-[#3DBB8E]" : "text-[#E4C97A]"}`}>{c.closing_fees_paid ? `Paid ${fmt(cf.total)}` : `Due ${fmt(cf.total)}`}</span>
          <span className="text-[#8A8A8A]">Monthly installments</span>
          <span className="text-right font-mono text-[#B8C4B8]">{paidCount}/{sched.length} paid{nextDue ? ` · next ${fmt(nextDue.amount)} due ${nextDue.due_date}` : " · complete"}</span>
          <span className="text-[#8A8A8A]">Auto-debit</span>
          <span className={`text-right font-mono ${ap.card_last4 || ap.method ? "text-[#3DBB8E]" : "text-[#8A8A8A]"}`}>{ap.card_last4 || ap.method ? `Active · ${fmt(ap.amount)}/mo` : "Not set up"}</span>
        </div>
      )}
      <div className="flex items-center gap-2 flex-wrap">
        <Btn label={onFile ? "Update Card / Bank" : "Set Up Card / Bank"} path="payment-setup" icon={CreditCard} testid={`broker-pay-setup-${c.id}`} primary />
        {!c.closing_fees_paid && <Btn label="Pay Closing Costs" path="pay-closing-fees" icon={Landmark} testid={`broker-pay-closing-${c.id}`} />}
        {c.closing_fees_paid && <span className="text-[11px] text-[#3DBB8E] inline-flex items-center gap-1"><CheckCircle2 size={12} /> Closing costs paid</span>}
        <Btn label="Pay Monthly Payment" path="pay-installment" icon={CreditCard} testid={`broker-pay-installment-${c.id}`} />
      </div>
    </div>
  );
};

export const printBrokerReceipt = (r) => {
  const money = (n) => "$" + Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const kindLabel = r.kind === "closing_fees" ? "Closing Costs" : (r.installment_n ? `Monthly Payment #${r.installment_n}` : "Payment");
  const row = (k, v) => (v ? `<tr><td class="k">${k}</td><td>${v}</td></tr>` : "");
  const rows = (r.items || []).map((it) => `<tr><td>${it.label}</td><td style="text-align:right">${money(it.amount)}</td></tr>`).join("");
  const w = window.open("", "_blank", "width=760,height=900");
  if (!w) return;
  w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>Receipt ${r.receipt_no}</title>
<style>@page{size:letter;margin:.7in}body{font-family:Georgia,serif;color:#141410;padding:24px 30px}
.wm{letter-spacing:.22em;font-weight:800;color:#0B4636;font-size:15px}.eb{font-size:10px;letter-spacing:.14em;text-transform:uppercase;color:#8a6a10;font-weight:700;margin-top:2px}
h1{font-size:24px;margin:10px 0 4px}hr{border:none;border-top:2px solid #0B4636;margin:8px 0 16px}
table{width:100%;border-collapse:collapse;font-size:13px;margin:6px 0}td{border:1px solid #e2e2da;padding:7px 10px}.k{background:#f6f6f1;font-weight:600;width:42%}
.amt{text-align:right;font-size:17px;font-weight:800;margin-top:12px}.note{font-size:11px;color:#6c6c64;margin-top:18px;line-height:1.5}
.btn{margin:16px 0}@media print{.btn{display:none}}</style></head><body>
<div class="btn"><button onclick="window.print()">Print / Save PDF</button></div>
<div class="wm">SEUTRADE FINANCIAL SERVICES</div><div class="eb">Private Lender Services · Payment Receipt</div>
<h1>Payment Receipt</h1><hr/>
<table>${row("Receipt No.", r.receipt_no)}${row("Date", r.issued_at)}${row("Seutrade Account No.", r.seutrade_account_no)}${row("Payment For", kindLabel)}${row("Borrower", r.borrower_name)}${row("Email", r.borrower_email)}${row("Phone", r.borrower_phone)}${row("Address", r.borrower_address)}${row("Property / Collateral", r.property)}${row("Payment Method", r.payment_method)}<tr><td class="k">Reference</td><td style="font-family:monospace;font-size:11px">${String(r.ref || "").slice(0, 60)}</td></tr></table>
${rows ? `<table>${rows}</table>` : ""}
<div class="amt">Amount Paid: ${money(r.amount)} USD</div>
<p class="note">This receipt confirms funds received by Seutrade Financial Services for the item(s) above. Official electronic receipt — no signature required.</p>
</body></html>`);
  w.document.close();
};

const BrokerReceipts = ({ receipts }) => {
  if (!receipts || !receipts.length) return null;
  return (
    <div data-testid="broker-receipts">
      <h3 className="font-serif text-xl mb-4 flex items-center gap-2"><FileCheck2 size={17} className="text-[#3DBB8E]" /> Client Payment Receipts</h3>
      <div className="space-y-2">
        {receipts.map((r) => (
          <div key={r.id} className="flex items-center justify-between gap-4 bg-[#13130E] border border-[#2A2A22] rounded-lg px-5 py-3.5" data-testid={`broker-receipt-${r.id}`}>
            <div>
              <div className="text-[13px] text-[#ECECE4] font-semibold">
                {r.borrower_name} · {r.kind === "closing_fees" ? "Closing Costs" : (r.installment_n ? `Monthly Payment #${r.installment_n}` : "Payment")} · {fmt(r.amount)}
              </div>
              <div className="text-[11px] text-[#8A8A8A] mt-0.5">{r.receipt_no} · {r.seutrade_account_no} · {r.issued_at}{r.emailed ? " · ✉ emailed" : ""}</div>
            </div>
            <button onClick={() => printBrokerReceipt(r)} data-testid={`broker-receipt-view-${r.id}`}
              className="inline-flex items-center gap-1.5 border border-[#B8860B]/40 hover:border-[#B8860B] text-[#E4C97A] px-3.5 py-2 text-[10px] uppercase tracking-[0.12em] font-bold rounded-sm">
              <FileText size={13} /> View / Print
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

const BorrowerAccessPanel = ({ c, authAxios }) => {
  const [cred, setCred] = useState(null);
  const [busy, setBusy] = useState(false);
  const gen = async () => {
    if (cred && !window.confirm("Generate a NEW temporary password? The borrower's current password will stop working.")) return;
    setBusy(true);
    try {
      const r = await authAxios.post(`/broker/clients/${c.id}/borrower-access`);
      setCred(r.data.invite);
      toast.success("Borrower login ready — share the credentials below.");
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const copy = () => { navigator.clipboard?.writeText(`SEUTRADE FINANCIAL SERVICES borrower login (${window.location.origin}/borrower)\nEmail: ${cred.email}\nTemporary password: ${cred.temp_password}`); toast.success("Credentials copied."); };
  return (
    <div className="mt-4 bg-[#0F0F0F] border border-[#2A6B4F]/40 rounded-md p-3" data-testid={`broker-access-${c.id}`}>
      <div className="text-[10px] uppercase tracking-[0.12em] text-[#3DBB8E] font-bold mb-1">Borrower Login Access — so they can e-sign</div>
      <div className="text-[10.5px] text-[#8A8A8A] mb-2">Borrower: <span className="text-[#B8C4B8]">{c.borrower_email}</span>. Generate a temporary password and share it — the borrower signs in at <span className="text-[#B8C4B8]">/borrower</span> to e-sign their binder.</div>
      {cred && (
        <div className="mb-2 bg-[#0B1A14] border border-[#2A6B4F]/50 rounded-sm p-2.5 text-[12px]" data-testid={`broker-access-cred-${c.id}`}>
          <div>Email: <strong className="text-[#ECECE4]" data-testid={`broker-access-email-${c.id}`}>{cred.email}</strong></div>
          <div>Temporary password: <strong className="text-[#3DBB8E]" data-testid={`broker-access-pw-${c.id}`}>{cred.temp_password}</strong></div>
          <div className="text-[10px] text-[#8A8A8A] mt-1">Shown once — copy it now.</div>
        </div>
      )}
      <div className="flex items-center gap-2 flex-wrap">
        <button onClick={gen} disabled={busy} data-testid={`broker-access-gen-${c.id}`}
          className="inline-flex items-center gap-1.5 bg-[#2A6B4F] hover:bg-[#22593f] disabled:opacity-50 text-[#ECECE4] px-3 py-1.5 text-[10px] uppercase tracking-[0.1em] font-bold rounded-sm">
          {busy ? <Loader2 size={12} className="animate-spin" /> : <KeyRound size={12} />} {cred ? "Reset Borrower Password" : "Generate Borrower Password"}
        </button>
        {cred && <button onClick={copy} data-testid={`broker-access-copy-${c.id}`}
          className="inline-flex items-center gap-1.5 border border-[#3A3A2E] hover:border-[#3A3A3A] text-[#B8C4B8] px-3 py-1.5 text-[10px] uppercase tracking-[0.1em] rounded-sm"><Copy size={12} /> Copy Login</button>}
      </div>
    </div>
  );
};

const EditClientForm = ({ c, authAxios, onSaved, onCancel }) => {
  const [f, setF] = useState({
    borrower_full_name: c.borrower_name || "", borrower_email: c.borrower_email || "",
    borrower_phone: c.borrower_phone || "", borrower_address: c.borrower_address || "",
    borrower_dob: c.borrower_dob || "", co_borrower_name: c.co_borrower_name || "",
    co_borrower_email: c.co_borrower_email || "", co_borrower_phone: c.co_borrower_phone || "",
    co_borrower_address: c.co_borrower_address || "", co_borrower_ssn: c.co_borrower_ssn || "", co_borrower_dob: c.co_borrower_dob || "",
    employer: c.employer || "", annual_income: c.annual_income ?? "",
    property_or_collateral: c.property_or_collateral || "", property_state: c.property_state || "",
    property_county: c.property_county || "", legal_description: c.legal_description || "",
    tax_parcel_id: c.tax_parcel_id || "",
    note_structure: c.note_structure || "", property_use: c.property_use || "residential",
    occupancy: c.occupancy || "owner_occupied",
    current_lender: c.current_lender || "", current_lender_address: c.current_lender_address || "",
    account_number: c.account_number || "", current_balance: c.current_balance ?? "",
    counterparty_bank_name: c.counterparty_bank_name || "", counterparty_bank_aba: c.counterparty_bank_aba || "",
    counterparty_bank_account: c.counterparty_bank_account || "",
    current_rate: c.current_rate ?? "", current_payment: c.current_payment ?? "",
    term_months: c.term_months || 360,
    transaction_type: c.transaction_type || "refinance", mortgage_position: c.mortgage_position || "first",
    assumption_existing_rate: c.assumption_existing_rate ?? "", assumption_monthly_payment: c.assumption_monthly_payment ?? "",
    assumption_remaining_term: c.assumption_remaining_term ?? "", assumption_next_due_date: c.assumption_next_due_date || "",
    relocation_incentive: c.relocation_incentive ?? 500, vacate_date: c.vacate_date || "", deed_type: c.deed_type || "warranty",
    trust_name: c.trust_name || "",
    additional_debts: Array.isArray(c.additional_debts) ? c.additional_debts.map((d) => ({ ...d })) : [],
  });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const up = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const isAsm = f.transaction_type === "assumption";
  const save = async () => {
    setBusy(true); setMsg(null);
    try {
      await authAxios.put(`/broker/clients/${c.id}`, {
        ...f,
        current_balance: Number(f.current_balance || 0),
        current_rate: f.current_rate ? Number(f.current_rate) : null,
        current_payment: f.current_payment ? Number(f.current_payment) : null,
        term_months: Number(f.term_months || 360),
        annual_income: f.annual_income ? Number(f.annual_income) : null,
        transaction_type: f.transaction_type, debt_type: "mortgage",
        mortgage_position: f.mortgage_position || null,
        assumption_existing_rate: isAsm && f.assumption_existing_rate ? Number(f.assumption_existing_rate) : null,
        assumption_monthly_payment: isAsm && f.assumption_monthly_payment ? Number(f.assumption_monthly_payment) : null,
        assumption_remaining_term: isAsm && f.assumption_remaining_term ? Number(f.assumption_remaining_term) : null,
        assumption_next_due_date: isAsm ? (f.assumption_next_due_date || null) : null,
        relocation_incentive: isAsm ? Number(f.relocation_incentive || 500) : null,
        vacate_date: isAsm ? (f.vacate_date || null) : null,
        deed_type: isAsm ? (f.deed_type || "warranty") : null,
        checklist: c.broker_checklist || {},
        trust_name: f.trust_name || "",
        additional_debts: isAsm ? [] : (f.additional_debts || []).filter((d) => d.current_lender || d.current_balance).map((d) => ({ ...d, current_balance: Number(d.current_balance || 0) })),
      });
      onSaved && onSaved();
    } catch (e) { setMsg(errMsg(e)); setBusy(false); }
  };
  return (
    <div className="mt-4 border-t border-[#2A2A22] pt-4" data-testid={`broker-edit-form-${c.id}`}>
      <div className="text-[10px] uppercase tracking-[0.14em] text-[#C9A227] font-bold mb-2">Edit Client — changes rebuild the binder, schedule &amp; commission (a signed client must re-sign)</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div><Label>Full legal name</Label><Inp v={f.borrower_full_name} set={(v) => up("borrower_full_name", v)} testid={`bedit-name-${c.id}`} /></div>
        <div><Label>Borrower email</Label><Inp v={f.borrower_email} set={(v) => up("borrower_email", v)} testid={`bedit-email-${c.id}`} type="email" /></div>
        <div><Label>Phone</Label><Inp v={f.borrower_phone} set={(v) => up("borrower_phone", v)} testid={`bedit-phone-${c.id}`} /></div>
        <div><Label>Employer</Label><Inp v={f.employer} set={(v) => up("employer", v)} testid={`bedit-employer-${c.id}`} /></div>
      </div>
      <div className="text-[10px] uppercase tracking-[0.14em] text-[#C9A227] font-bold mb-2 mt-3">Borrower No. 2 — Co-Borrower / Second Party <span className="text-[#6f6f66] normal-case tracking-normal font-normal">(optional)</span></div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div><Label>Full legal name</Label><Inp v={f.co_borrower_name} set={(v) => up("co_borrower_name", v)} testid={`bedit-cob-name-${c.id}`} /></div>
        <div><Label>Email</Label><Inp v={f.co_borrower_email} set={(v) => up("co_borrower_email", v)} testid={`bedit-cob-email-${c.id}`} type="email" /></div>
        <div><Label>Phone</Label><Inp v={f.co_borrower_phone} set={(v) => up("co_borrower_phone", v)} testid={`bedit-cob-phone-${c.id}`} /></div>
        <div><Label>Date of birth</Label><Inp v={f.co_borrower_dob} set={(v) => up("co_borrower_dob", v)} testid={`bedit-cob-dob-${c.id}`} type="date" /></div>
        <div><Label>Mailing address</Label><Inp v={f.co_borrower_address} set={(v) => up("co_borrower_address", v)} testid={`bedit-cob-address-${c.id}`} /></div>
        <div><Label>Social Security No.</Label><Inp v={f.co_borrower_ssn} set={(v) => up("co_borrower_ssn", v)} testid={`bedit-cob-ssn-${c.id}`} placeholder="XXX-XX-XXXX" /></div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div><Label>Transaction type</Label>
          <Sel v={f.transaction_type} set={(v) => up("transaction_type", v)} testid={`bedit-tx-type-${c.id}`}
            opts={[["refinance", "Refinance (0% note)"], ["consolidation", "Consolidation (0% note)"], ["assumption", "Mortgage Assumption (subject-to)"], ["foreclosure", "Foreclosure / Standstill (180-day)"]]} /></div>
        <div><Label>Mortgage lien position</Label>
          <Sel v={f.mortgage_position} set={(v) => up("mortgage_position", v)} testid={`bedit-mortgage-position-${c.id}`}
            opts={[["first", "First (1st)"], ["second", "Second (2nd)"], ["third", "Third (3rd)"]]} /></div>
      </div>
      {isAsm && (
        <div className="mb-3 rounded-md border border-[#C9A227]/40 bg-[#1a1710] p-4" data-testid={`bedit-assumption-fields-${c.id}`}>
          <div className="text-[10px] uppercase tracking-[0.14em] text-[#C9A227] font-bold mb-2">Mortgage Assumption (subject-to) — existing note terms</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label>Existing rate (%)</Label><Inp v={f.assumption_existing_rate} set={(v) => up("assumption_existing_rate", v)} testid={`bedit-asm-rate-${c.id}`} type="number" /></div>
            <div><Label>Existing monthly payment ($)</Label><Inp v={f.assumption_monthly_payment} set={(v) => up("assumption_monthly_payment", v)} testid={`bedit-asm-monthly-${c.id}`} type="number" /></div>
            <div><Label>Remaining term (months)</Label><Inp v={f.assumption_remaining_term} set={(v) => up("assumption_remaining_term", v)} testid={`bedit-asm-term-${c.id}`} type="number" /></div>
            <div><Label>Next payment due</Label><Inp v={f.assumption_next_due_date} set={(v) => up("assumption_next_due_date", v)} testid={`bedit-asm-nextdue-${c.id}`} type="date" /></div>
            <div><Label>Cash-for-keys / relocation ($)</Label><Inp v={f.relocation_incentive} set={(v) => up("relocation_incentive", v)} testid={`bedit-asm-relocation-${c.id}`} type="number" /></div>
            <div><Label>Vacate date</Label><Inp v={f.vacate_date} set={(v) => up("vacate_date", v)} testid={`bedit-asm-vacate-${c.id}`} type="date" /></div>
            <div><Label>Deed type</Label>
              <Sel v={f.deed_type} set={(v) => up("deed_type", v)} testid={`bedit-asm-deed-${c.id}`}
                opts={[["warranty", "Warranty Deed"], ["quitclaim", "Quitclaim Deed"]]} /></div>
          </div>
        </div>
      )}
      <Label>Property address / description</Label>
      <Inp v={f.property_or_collateral} set={(v) => up("property_or_collateral", v)} testid={`bedit-property-${c.id}`} />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div><Label>Property state</Label>
          <Sel v={f.property_state} set={(v) => { up("property_state", v); up("note_structure", recommendedNoteStructure(v)); }} testid={`bedit-property-state-${c.id}`}
            opts={[["", "Select state…"], ...US_STATES]} /></div>
        <div><Label>Property county</Label><Inp v={f.property_county} set={(v) => up("property_county", v)} testid={`bedit-property-county-${c.id}`} /></div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div><Label>Tax Parcel / APN</Label><Inp v={f.tax_parcel_id} set={(v) => up("tax_parcel_id", v)} testid={`bedit-parcel-${c.id}`} /></div>
      </div>
      <Label>Legal description (Exhibit A)</Label>
      <textarea value={f.legal_description} onChange={(e) => up("legal_description", e.target.value)} data-testid={`bedit-legal-${c.id}`} rows={2}
        className="w-full bg-[#0A0A0A] border border-[#2A2A22] rounded-sm px-3 py-2 text-[12px] text-[#ECECE4] mb-2" />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div><Label>Current lender</Label><Inp v={f.current_lender} set={(v) => up("current_lender", v)} testid={`bedit-lender-${c.id}`} /></div>
        <div><Label>Lender address</Label><Inp v={f.current_lender_address} set={(v) => up("current_lender_address", v)} testid={`bedit-lender-address-${c.id}`} /></div>
        <div><Label>Account / loan no.</Label><Inp v={f.account_number} set={(v) => up("account_number", v)} testid={`bedit-account-${c.id}`} /></div>
        <div><Label>Term (months)</Label><Inp v={f.term_months} set={(v) => up("term_months", v)} testid={`bedit-term-${c.id}`} type="number" /></div>
        <div><Label>Current balance</Label><Inp v={f.current_balance} set={(v) => up("current_balance", v)} testid={`bedit-balance-${c.id}`} type="number" /></div>
        <div><Label>Current payment</Label><Inp v={f.current_payment} set={(v) => up("current_payment", v)} testid={`bedit-payment-${c.id}`} type="number" /></div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid={`bedit-counterparty-bank-${c.id}`}>
        <div><Label>Counterparty bank</Label><Inp v={f.counterparty_bank_name} set={(v) => up("counterparty_bank_name", v)} testid={`bedit-cp-bank-name-${c.id}`} /></div>
        <div><Label>Counterparty ABA</Label><Inp v={f.counterparty_bank_aba} set={(v) => up("counterparty_bank_aba", v)} testid={`bedit-cp-bank-aba-${c.id}`} /></div>
        <div><Label>Counterparty acct no.</Label><Inp v={f.counterparty_bank_account} set={(v) => up("counterparty_bank_account", v)} testid={`bedit-cp-bank-account-${c.id}`} /></div>
      </div>
      <div className="flex items-center gap-2 mt-3">
        <button onClick={save} disabled={busy || !f.borrower_full_name || !f.borrower_email || !f.current_lender || !f.current_balance} data-testid={`bedit-save-${c.id}`}
          className="inline-flex items-center gap-2 bg-[#C9A227] hover:bg-[#b8901f] disabled:opacity-40 text-[#0A0A0A] px-4 py-2 text-[11px] uppercase tracking-[0.16em] font-bold rounded-sm">
          {busy ? <Loader2 size={13} className="animate-spin" /> : null} Save Changes
        </button>
        <button onClick={onCancel} data-testid={`bedit-cancel-${c.id}`}
          className="inline-flex items-center gap-1.5 border border-[#2A2A22] hover:border-[#3A3A3A] text-[#9A9A90] px-4 py-2 text-[11px] uppercase tracking-[0.14em] rounded-sm"><X size={13} /> Cancel</button>
      </div>
      {msg && <div className="text-[11px] text-[#FF8585] mt-2" data-testid={`bedit-error-${c.id}`}>✗ {msg}</div>}
    </div>
  );
};

const Label = ({ children }) => <div className="text-[10px] uppercase tracking-[0.14em] text-[#8A8A8A] mb-1.5 mt-2">{children}</div>;
const Inp = ({ v, set, testid, type = "text", placeholder }) => (
  <input type={type} value={v} placeholder={placeholder} onChange={(e) => set(e.target.value)} data-testid={testid}
    className="w-full bg-[#0F0F0F] border border-[#3A3A3A] px-3 py-2 rounded-sm text-[13px]" />
);
const Sel = ({ v, set, testid, opts, small }) => (
  <select value={v} onChange={(e) => set(e.target.value)} data-testid={testid}
    className={`bg-[#0F0F0F] border border-[#3A3A3A] rounded-sm text-[12px] ${small ? "px-2 py-1.5" : "w-full px-3 py-2 text-[13px]"}`}>
    {opts.map(([val, lbl]) => <option key={val} value={val}>{lbl}</option>)}
  </select>
);
const StatusPill = ({ status }) => {
  const cls = { approved: "text-[#3DBB8E] border-[#0B6B4F]/60 bg-[#0B6B4F]/15", pending: "text-[#E4C97A] border-[#B8860B]/50 bg-[#B8860B]/10", rejected: "text-[#FF8585] border-[#9E2A2B]/60 bg-[#9E2A2B]/15", suspended: "text-[#FF8585] border-[#9E2A2B]/60 bg-[#9E2A2B]/15", terminated: "text-[#FF8585] border-[#9E2A2B]/60 bg-[#9E2A2B]/25" }[status] || "";
  return <span className={`px-2 py-0.5 text-[10px] uppercase tracking-[0.12em] border rounded-sm ${cls}`} data-testid="broker-status-pill">{status}</span>;
};
const Banner = ({ icon: Icon, cls, t, d, note }) => (
  <div className={`border rounded-lg p-6 flex items-start gap-3 ${cls}`} data-testid="broker-status-banner">
    <Icon size={20} className="mt-0.5 shrink-0" />
    <div>
      <div className="font-serif text-lg">{t}</div>
      <div className="text-[13px] mt-1 opacity-90 leading-relaxed">{d}</div>
      {note && <div className="text-[12px] mt-2 italic">Note from desk: {note}</div>}
    </div>
  </div>
);

const DeedCopyField = ({ label, value }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => navigator.clipboard.writeText(String(value ?? "")).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1200); });
  return (
    <div className="flex items-start gap-2 py-1.5 border-b border-[#1C1C16]" data-testid="deed-copy-field">
      <div className="w-56 shrink-0 text-[10px] uppercase tracking-[0.08em] text-[#8A8A8A] pt-1">{label}</div>
      <div className="flex-1 text-[12px] text-[#ECECE4] break-words whitespace-pre-wrap">{value || <span className="text-[#5C5C5C]">—</span>}</div>
      <button onClick={copy} className="shrink-0 inline-flex items-center gap-1 text-[10px] text-[#9DB4E8] hover:text-[#c3d3f5] pt-0.5">
        {copied ? <><CheckCircle2 size={12} /> Copied</> : <><Copy size={12} /> Copy</>}
      </button>
    </div>
  );
};

const DeedWorksheetModal = ({ worksheet, onClose }) => (
  <div className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center overflow-y-auto p-4" onClick={onClose} data-testid="deed-worksheet-modal">
    <div className="bg-[#0F0F0A] border border-[#2A2A22] rounded-xl max-w-2xl w-full my-8" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-between px-6 py-4 border-b border-[#2A2A22] sticky top-0 bg-[#0F0F0A] z-10">
        <div>
          <div className="text-[10px] uppercase tracking-[0.16em] text-[#C9A227] font-bold">Deed Recording Worksheet</div>
          <div className="text-[13px] text-[#ECECE4]">{worksheet.instrument}</div>
        </div>
        <button onClick={onClose} data-testid="deed-worksheet-close" className="text-[#8A8A8A] hover:text-[#ECECE4]"><X size={18} /></button>
      </div>
      <div className="px-6 py-4">
        {Object.entries(worksheet.sections).map(([section, fields]) => (
          <div key={section} className="mb-4">
            <div className="text-[11px] uppercase tracking-[0.14em] text-[#C9A227] mb-1">{section}</div>
            {Object.entries(fields).map(([k, v]) => <DeedCopyField key={k} label={k} value={v} />)}
          </div>
        ))}
        {worksheet.notes && <p className="text-[11px] text-[#8A8A8A] leading-relaxed mt-2">{worksheet.notes}</p>}
      </div>
    </div>
  </div>
);
